import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  FileText, Plus, Save, Upload, Link as LinkIcon,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading1, Heading2, Heading3, Quote, Image as ImageIcon, Table as TableIcon,
  Type, Strikethrough, Code, AlignLeft, AlignCenter, AlignRight, X,
  Share2, Copy as CopyIcon, Scissors, Clipboard, ChevronDown,
  CheckSquare, Highlighter, MessageSquare, History, Trash2 as TrashIcon, Edit3,
  Download, Minus, Maximize2, Minimize2, BarChart2, Columns, Eye, Layout, Sparkles, Pen,
} from "lucide-react";
import HandwritingCanvas, { MiniCanvas } from "./HandwritingCanvas";
import { toast } from "sonner";
import { confirmDelete } from "./ConfirmDialog";
import { ThemedSelect } from "./ThemedSelect";
import { TextColorControl } from "./DocColorControls";
import { HighlightRoster } from "./HighlightRoster";
import { BulletShapePicker } from "./BulletShapePicker";
import { FontPicker } from "./FontPicker";
import DocumentComments from "./DocumentComments";
import DocumentVersionHistory from "./DocumentVersionHistory";
import DocumentTrash from "./DocumentTrash";

/** Google Docs-style tabbed navbar: doc tabs on top, section toolbar below */

/** Unified History panel — two sub-tabs: Version History + Recently Deleted */
function UnifiedHistoryPanel({ active, workspaceId, initialTab = "history", onClose, onRestored }) {
  const [tab, setTab] = React.useState(initialTab);
  return (
    <>
      {/* Unified backdrop */}
      <div className="fixed inset-0 z-[250] bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-[260] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh] pointer-events-auto" onClick={e => e.stopPropagation()}>
          {/* Tab bar */}
          <div className="flex items-center border-b border-[#2A2A2A] px-4 pt-3 gap-4 bg-[#0D0D0D]">
            <button onClick={() => setTab("history")} className={`pb-2.5 text-xs font-medium border-b-2 -mb-px transition-colors ${tab === "history" ? "border-[#5A90F5] text-[#EDEDED]" : "border-transparent text-[#A1A1AA] hover:text-[#EDEDED]"}`}>Version History</button>
            <button onClick={() => setTab("trash")} className={`pb-2.5 text-xs font-medium border-b-2 -mb-px transition-colors ${tab === "trash" ? "border-[#5A90F5] text-[#EDEDED]" : "border-transparent text-[#A1A1AA] hover:text-[#EDEDED]"}`}>Recently Deleted</button>
            <div className="flex-1" />
            <button onClick={onClose} className="mb-2 p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {tab === "history" && !active && <p className="text-xs text-[#52525B] text-center py-6">Open a document to view its history.</p>}
            {tab === "history" && active && <DocHistoryList docId={active.doc_id} onRestored={onRestored} onClose={onClose} />}
            {tab === "trash" && <DocTrashList workspaceId={workspaceId || active?.workspace_id} onRestored={onRestored} onClose={onClose} />}
          </div>
        </div>
      </div>
    </>
  );
}

function DocHistoryList({ docId, onRestored, onClose }) {
  const [versions, setVersions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [restoring, setRestoring] = React.useState(null);
  React.useEffect(() => {
    setLoading(true);
    api.get(`/documents/${docId}/versions`).then(({ data }) => setVersions(data || [])).catch(() => setVersions([])).finally(() => setLoading(false));
  }, [docId]);
  if (loading) return <div className="text-xs text-[#A1A1AA] text-center py-6">Loading…</div>;
  if (!versions.length) return <div className="text-xs text-[#A1A1AA] text-center py-6">No saved versions yet.</div>;
  return versions.map(v => (
    <div key={v.version_id} className="flex items-center gap-3 px-3 py-2 rounded border border-[#2A2A2A] bg-[#0A0A0A]">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[#EDEDED] truncate">{v.label || new Date(v.created_at).toLocaleString()}</div>
        <div className="text-[10px] text-[#52525B]">{new Date(v.created_at).toLocaleString()}</div>
      </div>
      <button onClick={async () => {
        setRestoring(v.version_id);
        try { await api.post(`/documents/${docId}/versions/${v.version_id}/restore`); onRestored?.(); onClose?.(); } catch { toast.error("Restore failed"); }
        setRestoring(null);
      }} disabled={restoring === v.version_id} className="text-[10px] px-2 py-1 rounded bg-[#1a1a1a] hover:bg-[#2a2a2a] disabled:opacity-50">
        {restoring === v.version_id ? "Restoring…" : "Restore"}
      </button>
    </div>
  ));
}

function DocTrashList({ onRestored, onClose, workspaceId }) {
  const [docs, setDocs] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetches = [
      api.get("/documents/trash").then(({ data }) => setDocs(data || [])).catch(() => {}),
      workspaceId
        ? api.get(`/workspaces/${workspaceId}/events/trash`).then(({ data }) => setEvents(data || [])).catch(() => {})
        : Promise.resolve(),
    ];
    Promise.all(fetches).finally(() => setLoading(false));
  }, [workspaceId]);

  if (loading) return <div className="text-xs text-[#A1A1AA] text-center py-6">Loading…</div>;
  if (!docs.length && !events.length) return <div className="text-xs text-[#A1A1AA] text-center py-6">Recently deleted is empty.</div>;

  return (
    <div className="space-y-2">
      {events.map(ev => (
        <div key={ev.event_id} className="flex items-center gap-3 px-3 py-2 rounded border border-[#2A2A2A] bg-[#0A0A0A]">
          <div className="w-2 h-2 rounded-full flex-shrink-0 bg-[#5A90F5]" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-[#EDEDED] truncate">{ev.title || "Untitled node"}</div>
            <div className="text-[10px] text-[#52525B]">Node · Deleted {ev.deleted_at ? new Date(ev.deleted_at).toLocaleString() : ""}</div>
          </div>
          <button onClick={async () => {
            try { await api.put(`/workspaces/${ev.workspace_id}/events/${ev.event_id}`, { deleted: false }); setEvents(p => p.filter(x => x.event_id !== ev.event_id)); onRestored?.(); } catch { toast.error("Restore failed"); }
          }} className="text-[10px] px-2 py-1 rounded bg-[#1a1a1a] hover:bg-[#2a2a2a]">Restore</button>
          <button onClick={async () => {
            try { await api.delete(`/workspaces/${ev.workspace_id}/events/${ev.event_id}?permanent=true`); setEvents(p => p.filter(x => x.event_id !== ev.event_id)); } catch { toast.error("Delete failed"); }
          }} className="text-[10px] px-2 py-1 rounded text-[#FF3B30] hover:bg-[#FF3B30]/10">Delete</button>
        </div>
      ))}
      {docs.map(d => (
        <div key={d.doc_id} className="flex items-center gap-3 px-3 py-2 rounded border border-[#2A2A2A] bg-[#0A0A0A]">
          <div className="w-2 h-2 rounded-sm flex-shrink-0 bg-[#A1A1AA]" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-[#EDEDED] truncate">{d.name || "Untitled"}</div>
            <div className="text-[10px] text-[#52525B]">Document · Deleted {d.deleted_at ? new Date(d.deleted_at).toLocaleString() : ""}</div>
          </div>
          <button onClick={async () => {
            try { await api.post(`/documents/${d.doc_id}/restore`); setDocs(prev => prev.filter(x => x.doc_id !== d.doc_id)); onRestored?.(); } catch { toast.error("Restore failed"); }
          }} className="text-[10px] px-2 py-1 rounded bg-[#1a1a1a] hover:bg-[#2a2a2a]">Restore</button>
          <button onClick={async () => {
            try { await api.delete(`/documents/${d.doc_id}?permanent=true`); setDocs(prev => prev.filter(x => x.doc_id !== d.doc_id)); } catch { toast.error("Delete failed"); }
          }} className="text-[10px] px-2 py-1 rounded text-[#FF3B30] hover:bg-[#FF3B30]/10">Delete</button>
        </div>
      ))}
    </div>
  );
}
import DocumentStatsToast from "./DocumentStatsToast";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { useDocuments } from "../context/DocumentsContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { useShorthands } from "../context/ShorthandContext";
import MasterDocProps from "./MasterDocProps";
import DocumentTemplates from "./DocumentTemplates";

/**
 * Full-screen Documents workspace.
 *
 * Editor is always rich text. The kind selector was removed (user request);
 * link-only docs still render via their stored `kind === 'link'` but new
 * docs default to markdown rich text with no separate dropdown.
 *
 * Toolbar controls: Line height, Font size, Font family — now custom
 * type-in fields (no dropdowns). Heading buttons set actual heading sizes
 * via inline styles so H1/H2/H3 visibly differ in the contenteditable
 * surface. Right-click in the editor opens a Microsoft-Word-style context
 * menu with Cut / Copy / Paste / formatting shortcuts.
 */

function MasterDocPropsWithCtx({ doc, onSave }) {
  const [masterCtx, setMasterCtx] = useState(null);
  return (
    <>
      <MasterDocProps
        doc={doc}
        properties={doc.properties || []}
        onChange={onSave}
        onContextMenu={(e, prop, idx) => {
          e.preventDefault();
          setMasterCtx({ x: Math.min(e.clientX, window.innerWidth - 180), y: Math.min(e.clientY, window.innerHeight - 100), prop, idx });
        }}
      />
      {masterCtx && (
        <>
          <div className="fixed inset-0 z-[250]" onClick={() => setMasterCtx(null)} />
          <div className="fixed z-[251] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl p-1 min-w-[160px]"
            style={{ left: masterCtx.x, top: masterCtx.y }}>
            {masterCtx.prop && <div className="ctx-sub">{masterCtx.prop.key}</div>}
            <div className="ctx-item" onClick={() => {
              const newKey = window.prompt("Rename property", masterCtx.prop?.key || "");
              if (newKey?.trim()) {
                const props = [...(doc.properties || [])];
                props[masterCtx.idx] = { ...props[masterCtx.idx], key: newKey.trim() };
                onSave(props);
              }
              setMasterCtx(null);
            }}>
              <Edit3 className="w-3.5 h-3.5" /> Rename key
            </div>
            <div className="ctx-sep" />
            <div className="ctx-item danger" onClick={() => {
              onSave((doc.properties || []).filter((_, i) => i !== masterCtx.idx));
              setMasterCtx(null);
            }}>
              <TrashIcon className="w-3.5 h-3.5" /> Delete property
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── MultiPageEditor ────────────────────────────────────────────────────────────
// True multi-page editing. Each page is a fixed-height contentEditable div with
// overflow:hidden. Rebalancing is two-phase:
//   Phase 1 (onInput): push/pull content using existing page refs. If the last
//     page overflows, request a new page via setState.
//   Phase 2 (componentDidUpdate): fires after the new page mounts; retries
//     rebalance so content actually lands on the fresh page.
class MultiPageEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = { numPages: 1 };
    this.pageRefs   = [];          // DOM refs for each page editor div
    this.rebalanceScheduled = false;
  }

  componentDidUpdate(_, prevState) {
    // Phase 2: a new page was added — now its ref exists, so rebalance again
    if (this.state.numPages !== prevState.numPages) {
      this.rebalance(this.state.numPages - 2);
    }
  }

  /* Ensure a block-level element to move (wrap bare text nodes) */
  _wrapIfText(node) {
    if (!node) return node;
    if (node.nodeType === 3) { // text node
      const d = document.createElement('div');
      d.textContent = node.textContent;
      node.parentNode.replaceChild(d, node);
      return d;
    }
    return node;
  }

  rebalance(fromPage = 0) {
    // Prevent re-entrant calls within the same tick
    if (this.rebalanceScheduled) return;
    this.rebalanceScheduled = true;
    requestAnimationFrame(() => {
      this.rebalanceScheduled = false;
      this._doRebalance(fromPage);
    });
  }

  _doRebalance(fromPage) {
    if (!this.pageRefs[0]) return; // not yet mounted
    const n = this.state.numPages;

    // ── Phase 1a: push overflow from each page to the next ──────────────────
    for (let i = fromPage; i < n; i++) {
      const el = this.pageRefs[i];
      if (!el) continue;

      let pushIterations = 0;
      while (el.scrollHeight > el.clientHeight + 2) {
        pushIterations++;
        // Safety: if we've been looping too long, the element is likely taller
        // than the page itself — stop to prevent an infinite loop.
        if (pushIterations > 200) break;

        // Wrap bare text nodes first
        if (el.lastChild) this._wrapIfText(el.lastChild);
        const overflow = el.lastChild;
        if (!overflow) break;

        // If the overflow element itself is taller than the page, don't
        // keep creating new pages for it — just leave it in place.
        if (overflow.scrollHeight > el.clientHeight + 2) break;

        if (i === n - 1) {
          // Last existing page overflows → need a new page, but only if
          // we haven't already added too many pages in this rebalance cycle.
          if (n >= 200) break; // hard cap
          this.setState(prev => ({ numPages: prev.numPages + 1 }));
          return; // componentDidUpdate will call rebalance again
        }
        const next = this.pageRefs[i + 1];
        if (!next) break;
        next.insertBefore(overflow, next.firstChild); // move (don't clone)
      }
    }

    // ── Phase 1b: pull content back when a page has room ────────────────────
    for (let i = fromPage; i < n - 1; i++) {
      const el   = this.pageRefs[i];
      const next = this.pageRefs[i + 1];
      if (!el || !next) continue;
      // Don't pull back if current page is already at capacity
      if (el.scrollHeight > el.clientHeight + 2) continue;

      let pullIterations = 0;
      while (next.firstChild) {
        pullIterations++;
        if (pullIterations > 200) break; // safety cap
        const candidate = next.firstChild;
        el.appendChild(candidate);                    // try moving it
        if (el.scrollHeight > el.clientHeight + 2) {
          next.insertBefore(el.lastChild, next.firstChild); // put it back
          break;
        }
      }
    }

    // ── Phase 1c: drop empty trailing pages ─────────────────────────────────
    let live = n;
    while (live > 1) {
      const el = this.pageRefs[live - 1];
      if (el && el.innerHTML.trim() === '') live--;
      else break;
    }
    if (live !== n) this.setState({ numPages: live });
  }

  getHTML() {
    return this.pageRefs
      .slice(0, this.state.numPages)
      .filter(Boolean)
      .map(el => el.innerHTML)
      .join('<hr class="cresc-page-break"/>');
  }

  setHTML(html) {
    if (!this.pageRefs[0]) return;
    const parts = html.split(/<hr[^>]*cresc-page-break[^>]*\/?>/i);
    this.setState({ numPages: Math.max(1, parts.length) }, () => {
      parts.forEach((p, i) => {
        if (this.pageRefs[i]) this.pageRefs[i].innerHTML = p || '';
      });
    });
  }

  render() {
    const {
      pageOpts, PAGE_SIZES, PAGE_MARGINS, pageTextColor,
      onInput, onKeyDown, onBlur, onMouseUp, onKeyUp,
      onClick, onContextMenu, editorRef,
    } = this.props;
    const { numPages } = this.state;

    const sz    = PAGE_SIZES[pageOpts.size]  || PAGE_SIZES.letter;
    const mg    = PAGE_MARGINS[pageOpts.margins] ?? 72;
    const pageW = sz.w;
    const pageH = sz.h;
    const gap   = pageOpts.seamless ? 0 : 24;

    const col = pageOpts.color || '#1e1e1e';
    const pageBg = pageOpts.glass
      ? `rgba(${parseInt(col.slice(1,3),16)},${parseInt(col.slice(3,5),16)},${parseInt(col.slice(5,7),16)},0.85)`
      : col;

    return (
      <div style={{
        display:'flex', flexDirection:'column',
        alignItems:'center', padding:'24px 0 60px', gap,
      }}>
        {Array.from({ length: numPages }).map((_, i) => (
          <div key={i} style={{
            position:'relative', width:pageW, height:pageH, flexShrink:0,
            background: pageBg,
            backdropFilter: pageOpts.glass ? 'blur(4px)' : 'none',
            WebkitBackdropFilter: pageOpts.glass ? 'blur(4px)' : 'none',
            boxShadow: pageOpts.seamless
              ? (i === 0 ? '0 2px 32px rgba(0,0,0,0.6)' : 'none')
              : '0 2px 24px rgba(0,0,0,0.5)',
            borderRadius: pageOpts.seamless ? (i === 0 ? '2px 2px 0 0' : i === numPages-1 ? '0 0 2px 2px' : 0) : 2,
          }}>
            <div
              ref={el => {
                this.pageRefs[i] = el;
                if (i === 0 && editorRef) editorRef.current = el;
              }}
              contentEditable suppressContentEditableWarning
              data-page-idx={i}
              data-testid={i === 0 ? 'docs-rich-editor' : `docs-page-${i}`}
              style={{
                position:'absolute', inset:0,
                padding: mg, boxSizing:'border-box',
                overflow:'hidden',
                color: pageTextColor,
                outline:'none', zIndex:1,
                lineHeight:1.6, fontSize:14,
              }}
              onInput={e  => { if (onInput)  onInput(e);  this.rebalance(i); }}
              onKeyDown={onKeyDown}
              onKeyUp={onKeyUp}
              onBlur={onBlur}
              onMouseUp={onMouseUp}
              onClick={onClick}
              onContextMenu={onContextMenu}
            />
          </div>
        ))}
      </div>
    );
  }
}


export default function DocumentsView() {
  const { docs, update, activeId, setActiveId, tabs, openTab, closeTab, reload, patchDoc } = useDocuments();
  const { current: currentWs } = useWorkspace?.() || {};
  const { entries: shorthands } = useShorthands?.() || { entries: [] };

  // Live-update shorthand chips whenever a shorthand entry's expansion changes
  useEffect(() => {
    if (!editorRef.current || !shorthands?.length) return;
    const chips = editorRef.current.querySelectorAll(".cresc-shorthand-chip");
    chips.forEach(chip => {
      const entryId = chip.dataset.entryId;
      const entry = shorthands.find(s => s.entry_id === entryId);
      if (entry && chip.textContent !== entry.expansion) {
        chip.textContent = entry.expansion;
        chip.dataset.trigger = entry.trigger;
      }
    });
  }, [shorthands]);
  const [name, setName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [body, setBody] = useState("");
  const editorRef = useRef(null);
  const [dirty, setDirty] = useState(false);
  const [showHandwriting, setShowHandwriting] = useState(false);
  const [inkMode, setInkMode] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [currentBlock, setCurrentBlock] = useState("p");
  const [activeMenuTab, setActiveMenuTab] = useState("text"); // tracks File/Text/Insert/View

  // Keep a local copy of the active doc so we don't commit on every keystroke.
  const active = useMemo(() => docs.find((d) => d.doc_id === activeId), [docs, activeId]);

  const [activeSubtabId, setActiveSubtabId] = useState(null);

  // Auto-pick the first doc on mount / after reload.
  useEffect(() => {
    if (!activeId && docs.length > 0) setActiveId(docs[0].doc_id);
  }, [docs, activeId, setActiveId]);

  // When swapping active doc, reset sub-tab pick.
  useEffect(() => { setActiveSubtabId(null); }, [activeId]);

  // Listen for "open tab" requests dispatched from the sidebar (clicking a
  // nested tab under a doc opens that tab in the editor — user request).
  useEffect(() => {
    const onOpenTab = (e) => {
      const { doc_id, tab_id } = e.detail || {};
      if (!doc_id) return;
      setActiveId(doc_id);
      setTimeout(() => setActiveSubtabId(tab_id || null), 0);
    };
    const onOpenDoc = (e) => {
      const { doc_id } = e.detail || {};
      if (!doc_id) return;
      setActiveId(doc_id);
      openTab(doc_id);
    };
    window.addEventListener("crescendo:docs-open-tab", onOpenTab);
    window.addEventListener("crescendo:open-doc", onOpenDoc);
    return () => {
      window.removeEventListener("crescendo:docs-open-tab", onOpenTab);
      window.removeEventListener("crescendo:open-doc", onOpenDoc);
    };
  }, [setActiveId, openTab]);

  // When activeId changes, ensure it's in the tabs array.
  useEffect(() => {
    if (activeId) openTab(activeId);
  }, [activeId, openTab]);

  // Hydrate editor fields whenever the active doc OR active tab changes.
  // (User request: "Tab switching does not work in documents yet" — switch
  // the editor body to the selected sub-tab's body so each tab has its own
  // content.)
  const dirtyRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  // Mirror the editable text state into refs so the hydration effect's
  // cleanup can save the OLD document using the OLD state — not the
  // newly-active doc's state. Without these refs, switching tabs while
  // dirty would copy the editor's HTML into whichever doc just became
  // active (corrupting it / duplicating names — recurring user report:
  // "Switching tabs works really weird, opening wrong documents,
  // document names getting duplicated").
  const nameRef = useRef("");
  const linkUrlRef = useRef("");
  // Refs stay in sync via useEffect, but we also update them synchronously
  // in the load effect below so save() never captures a stale name.
  useEffect(() => { nameRef.current = name; }, [name]);
  useEffect(() => { linkUrlRef.current = linkUrl; }, [linkUrl]);

  const saveRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    setName(active.name || "");
    nameRef.current = active.name || ""; // sync immediately, not via useEffect
    if (active.kind === "link") {
      setLinkUrl(active.body || "");
      setBody("");
    } else {
      // Pull body from the selected tab if present, else from the doc body.
      const tab = activeSubtabId ? (active.tabs || []).find((t) => t.tab_id === activeSubtabId) : null;
      const savedBody = tab ? (tab.body || "") : (active.body || "");
      // Check localStorage buffer for unsaved edits — prefer buffer over server
      // body so users don't lose work when navigating away without saving.
      const bufKey = `cresc-doc-buffer:${active.doc_id}${activeSubtabId ? `:${activeSubtabId}` : ""}`;
      let initialBody = savedBody;
      try {
        const buf = localStorage.getItem(bufKey);
        if (buf !== null) {
          initialBody = buf; // restore unsaved work
        }
      } catch {}
      setBody(initialBody);
      setLinkUrl("");
      if (editorRef.current) {
        const hadFocus = document.activeElement === editorRef.current ||
                         editorRef.current.contains(document.activeElement);
        editorRef.current.innerHTML = initialBody;
        // Restore checked state from HTML attributes
        editorRef.current.querySelectorAll("input[type='checkbox'][checked]").forEach((cb) => {
          cb.checked = true;
          cb.closest(".cresc-checkitem")?.classList.add("cresc-checkitem--done");
        });
        // Ensure editor stays editable after innerHTML update
        editorRef.current.contentEditable = "true";
      }
    }
    setDirty(false);
  }, [active?.doc_id, activeSubtabId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCallback(async () => {
    if (!active) return;
    // Serialize checkbox checked state into HTML attributes before saving,
    // since innerHTML doesn't capture the current DOM checked property.
    if (editorRef.current) {
      editorRef.current.querySelectorAll("input[type='checkbox']").forEach((cb) => {
        if (cb.checked) cb.setAttribute("checked", "");
        else cb.removeAttribute("checked");
      });
      // Serialize cresc-checkitem--done class for completed items
      editorRef.current.querySelectorAll(".cresc-checkitem").forEach((item) => {
        const cb = item.querySelector("input[type='checkbox']");
        if (cb?.checked) item.classList.add("cresc-checkitem--done");
      });
    }
    const html = editorRef.current?.innerHTML ?? body;
    const payload = {
      name: nameRef.current || name || "Untitled", // use ref to avoid stale closure
      kind: active.kind || "markdown", // preserve original kind (master, link, markdown)
    };
    if (activeSubtabId) {
      // Persist the change to the active tab's body, leave doc.body alone.
      const updatedTabs = (active.tabs || []).map((t) =>
        t.tab_id === activeSubtabId ? { ...t, body: html } : t
      );
      payload.body = active.body || "";
      payload.tabs = updatedTabs;
    } else {
      payload.body = active.kind === "link" ? linkUrl : html;
      payload.tabs = active.tabs || [];
    }
    await update(active.doc_id, payload);
    setDirty(false);
    setLastSavedAt(new Date());
    // Clear the unsaved-work buffer now that we've persisted to the server
    try {
      const bufKey = `cresc-doc-buffer:${active.doc_id}${activeSubtabId ? `:${activeSubtabId}` : ""}`;
      localStorage.removeItem(bufKey);
    } catch {}
    /* silent: saved */
    window.dispatchEvent(new CustomEvent("crescendo:docs-changed"));
    try {
      const wsId = active.workspace_id;
      if (wsId) await api.post(`/workspaces/${wsId}/snapshots`);
    } catch { /* don't block UX on snapshot failure */ }
  }, [active, name, linkUrl, body, update, activeSubtabId]);
  saveRef.current = save;

  // Disconnect / refresh recovery — buffer the working body to
  // localStorage every 3s while dirty. On next mount of the same doc,
  // if the buffered version is newer than the server's `updated_at`,
  // surface a toast offering to restore it. This is a belt-and-braces
  // safety net on top of the regular auto-save (user request iter32:
  // "prevents losing progress if the page is refreshed, or the user
  // gets disconnected somehow").
  useEffect(() => {
    if (!active) return;
    const docId = active.doc_id;
    const tabId = activeSubtabId || "_root";
    const bufKey = `cresc-doc-buffer:${docId}:${tabId}`;

    // Recovery on mount — if a buffer exists and is newer than the
    // server's last update, prompt the user to restore.
    try {
      const buf = localStorage.getItem(bufKey);
      if (buf) {
        const parsed = JSON.parse(buf);
        const bufTime = parsed?.ts || 0;
        const serverTime = Date.parse(active.updated_at || "") || 0;
        if (bufTime > serverTime + 1500 && parsed.html !== editorRef.current?.innerHTML) {
          window.dispatchEvent(new CustomEvent("crescendo:doc-recovery-available", {
            detail: { docId, tabId, html: parsed.html, ts: bufTime, name: active.name },
          }));
        }
      }
    } catch {}

    // Buffer current dirty content every 3s.
    const tid = setInterval(() => {
      if (!dirtyRef.current) return;
      try {
        // Sync checkbox states into attributes before buffering
        editorRef.current?.querySelectorAll("input[type='checkbox']").forEach((cb) => {
          if (cb.checked) cb.setAttribute("checked", "");
          else cb.removeAttribute("checked");
        });
        localStorage.setItem(bufKey, JSON.stringify({
          html: editorRef.current?.innerHTML || "",
          ts: Date.now(),
        }));
      } catch {}
    }, 3000);
    return () => clearInterval(tid);
  }, [active?.doc_id, activeSubtabId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for "doc saved successfully" → wipe the buffer for that doc.
  useEffect(() => {
    const onSaved = () => {
      if (!active) return;
      const tabId = activeSubtabId || "_root";
      try { localStorage.removeItem(`cresc-doc-buffer:${active.doc_id}:${tabId}`); } catch {}
    };
    window.addEventListener("crescendo:docs-changed", onSaved);
    return () => window.removeEventListener("crescendo:docs-changed", onSaved);
  }, [active?.doc_id, activeSubtabId]);

  // Listen for restore action from the DocRecoveryToast.
  useEffect(() => {
    const onApplied = () => setDirty(true);
    window.addEventListener("crescendo:bullet-shape-applied", onApplied);
    return () => window.removeEventListener("crescendo:bullet-shape-applied", onApplied);
  }, []);

  // Listen for restore action from the DocRecoveryToast.
  useEffect(() => {
    const onRestore = (e) => {
      if (!active || e.detail?.docId !== active.doc_id) return;
      if (editorRef.current) editorRef.current.innerHTML = e.detail.html || "";
      setBody(e.detail.html || "");
      setDirty(true);
      // Trigger a save so the recovered version makes it back to the
      // server immediately.
      setTimeout(() => { try { saveRef.current && saveRef.current(); } catch {} }, 200);
    };
    window.addEventListener("crescendo:doc-recovery-restore", onRestore);
    return () => window.removeEventListener("crescendo:doc-recovery-restore", onRestore);
  }, [active?.doc_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter39 user request: "When the document is being typed on and is
  // suddenly exited, the whole document gets wiped, this is likely an
  // error when auto-saving. The document should not auto-save at all,
  // it should keep it in memory for when the user comes back to save
  // the document again."
  //
  // ALL auto-save paths are now disabled:
  //   - The blur/visibilitychange/beforeunload flush is gone.
  //   - The 5s debounced save effect is gone.
  //   - The cleanup-on-tab-switch save in the hydration effect is gone.
  // Only explicit user actions save: Ctrl+S, the Save button, or
  // restoring from history. Dirty state lives in React state (in
  // memory) and the localStorage buffer (already wired earlier) so
  // edits are recoverable across reloads but never silently committed.

  // Cmd/Ctrl+S — the only auto-trigger that survives.
  // Cmd/Ctrl+A — select all within the editor, not the whole page.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a") {
        // Only intercept when focus is inside the editor
        if (editorRef.current && editorRef.current.contains(document.activeElement || document.querySelector(":focus"))) {
          e.preventDefault();
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  // Iter34: Comments side-panel toggle.
  const [commentsOpen, setCommentsOpen] = useState(false);
  // Iter37: Document protection — version history modal + trash bin.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  // @mention picker
  const [mentionPicker, setMentionPicker] = useState(null); // { x, y, query }
  const [wsMembers, setWsMembers] = useState([]);

  // Global history button in sidebar tray opens this panel
  useEffect(() => {
    const on = () => setHistoryOpen(true);
    window.addEventListener("crescendo:open-history", on);
    return () => window.removeEventListener("crescendo:open-history", on);
  }, []);

  // Collapsible top bar (user request: "Document navbar should be
  // collapsible"). Persists per-tab in component state — start expanded.
  const [topBarCollapsed, setTopBarCollapsed] = useState(false);

  // Track whether the user explicitly set a text color so the highlight
  // contrast-flip behaviour respects their choice.
  const userPickedTextColorRef = useRef(false);
  const [, setLastForeColor] = useState(null);

  // Cached selection range — used by the image-insert flow to survive the
  // OS file-picker (which steals focus and clears the contenteditable
  // selection). Saved on mousedown of the image button, restored before
  // insertHTML.
  const savedRangeRef = useRef(null);

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    setDirty(true);
    editorRef.current?.focus();
  };

  // Apply heading with explicit, visibly-different font sizes (browser
  // default execCommand("formatBlock") wraps in <h1>/<h2>/<h3> but we also
  // set inline font-size for guaranteed scaling inside the editor body).
  // Iter35 user request: "Set as Standard" — copies the visual style
  // (font-family, font-size, font-weight, color, line-height) of the
  // currently-selected text and uses it as the canonical style for the
  // surrounding block's tag (H1/H2/H3/P). Mirrors Google Docs'
  // "Update [heading] to match" feature.
  const applySetAsStandard = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    let node = range.startContainer;
    if (node.nodeType === 3) node = node.parentNode;
    // Walk up to the block-level ancestor to determine which "level"
    // we're standardizing (h1, h2, h3, or p).
    let block = node;
    while (block && block !== editorRef.current && getComputedStyle(block).display === "inline") {
      block = block.parentNode;
    }
    if (!block || block === editorRef.current) {
      toast.error("Place cursor inside a heading or paragraph first.");
      return;
    }
    const tag = block.tagName?.toLowerCase();
    if (!["h1", "h2", "h3", "p"].includes(tag)) {
      toast.error("Set Standard works on H1/H2/H3/Body lines.");
      return;
    }
    // Pull computed style from the selection's actual node (not block) so
    // user-applied font + size are captured.
    const cs = getComputedStyle(node);
    const standardCSS = {
      fontFamily: cs.fontFamily,
      fontSize: cs.fontSize,
      fontWeight: cs.fontWeight,
      color: cs.color,
      lineHeight: cs.lineHeight !== "normal" ? cs.lineHeight : "",
    };
    // Apply to every same-tag element in this editor.
    if (!editorRef.current) return;
    editorRef.current.querySelectorAll(tag).forEach((el) => {
      Object.assign(el.style, standardCSS);
    });
    toast.success(`Set as standard ${tag.toUpperCase()}`);
    setDirty(true);
  };

  const updateCurrentBlock = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let node = sel.anchorNode;
    if (node?.nodeType === 3) node = node.parentElement;
    while (node && node !== editorRef.current) {
      const tag = node.tagName?.toLowerCase();
      if (tag === "h1" || tag === "h2" || tag === "h3") { setCurrentBlock(tag); return; }
      if (tag === "p" || tag === "div" || tag === "blockquote" || tag === "pre") { setCurrentBlock(tag); return; }
      node = node.parentElement;
    }
    setCurrentBlock("p");
  };

  const applyHeading = (level) => {
    document.execCommand("styleWithCSS", false, true);
    if (level === "p") {
      // Iter35 user request: "The body button does not convert text to body
      // text." Root cause: formatBlock=p doesn't strip the inline font-size
      // and font-weight that previous heading applications baked into
      // descendant <span>s and the heading element itself. Walk the block
      // and clear those styles so the body button is a true reset.
      exec("formatBlock", "p");
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      let node = sel.getRangeAt(0).startContainer;
      while (node && node.nodeType !== 1) node = node.parentNode;
      // Walk up to the block-level paragraph.
      while (node && node.tagName !== "P" && node !== editorRef.current) node = node.parentNode;
      if (!node || node.tagName !== "P") return;
      // Reset the block's own size/weight to defaults.
      node.style.fontSize = "";
      node.style.fontWeight = "";
      node.style.lineHeight = "";
      node.style.margin = "";
      // Strip inline font-size + font-weight from descendants.
      node.querySelectorAll("[style*='font-size'], [style*='font-weight']").forEach((el) => {
        el.style.fontSize = "";
        el.style.fontWeight = "";
        if (!el.getAttribute("style")) {
          const parent = el.parentNode;
          while (el.firstChild) parent.insertBefore(el.firstChild, el);
          parent.removeChild(el);
        }
      });
      setDirty(true);
      return;
    }
    exec("formatBlock", level);
    // Walk up to the heading element and set explicit font-size + weight.
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let node = sel.getRangeAt(0).startContainer;
    while (node && node.nodeType !== 1) node = node.parentNode;
    while (node && !/^H[1-3]$/.test(node.tagName) && node !== editorRef.current) node = node.parentNode;
    if (!node || !/^H[1-3]$/.test(node.tagName)) return;
    const sizes = { H1: "32px", H2: "24px", H3: "19px" };
    node.style.fontSize = sizes[node.tagName] || "";
    node.style.fontWeight = "700";
    node.style.lineHeight = "1.25";
    node.style.margin = "0.6em 0 0.3em";
    // Strip any inline font-size that earlier per-character formatting
    // baked into descendant <span>s — they would otherwise override the
    // heading's font-size and break the visible "make this an H1" UX
    // (user bug: "Setting text as different headings does not change the
    // font size").
    node.querySelectorAll("[style*='font-size']").forEach((el) => {
      el.style.fontSize = "";
      // If the inner span is now empty of declarations, unwrap it so we
      // don't litter the DOM with redundant wrappers.
      if (!el.getAttribute("style")) {
        const parent = el.parentNode;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      }
    });
    setDirty(true);
  };

  // Apply quote / code styling (so user-visible cues match — quote = white
  // left line + italic; code = grey bg wrapper).
  const applyQuote = () => {
    exec("formatBlock", "blockquote");
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let node = sel.getRangeAt(0).startContainer;
    while (node && (node.nodeType !== 1 || node.tagName !== "BLOCKQUOTE") && node !== editorRef.current) node = node.parentNode;
    if (!node || node.tagName !== "BLOCKQUOTE") return;
    node.style.borderLeft = "3px solid #FFFFFF";
    node.style.paddingLeft = "12px";
    node.style.fontStyle = "italic";
    node.style.color = "#D4D4D8";
    node.style.margin = "0.6em 0";
  };
  const applyCode = () => {
    exec("formatBlock", "pre");
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let node = sel.getRangeAt(0).startContainer;
    while (node && (node.nodeType !== 1 || node.tagName !== "PRE") && node !== editorRef.current) node = node.parentNode;
    if (!node || node.tagName !== "PRE") return;
    node.style.background = "#1a1a1a";
    node.style.border = "1px solid #2A2A2A";
    node.style.borderRadius = "6px";
    node.style.padding = "10px 12px";
    node.style.fontFamily = "Menlo, ui-monospace, monospace";
    node.style.fontSize = "12px";
    node.style.whiteSpace = "pre-wrap";
    node.style.color = "#E5E7EB";
    node.style.margin = "0.6em 0";
  };

  // Custom type-in fields for line / size / font.
  const [lineHeight, setLineHeight] = useState("1.5");
  const [fontSizePx, setFontSizePx] = useState("");
  // Iter39: separate draft state for the font-size input so typing
  // doesn't immediately clobber the selection on every keystroke.
  const [fontSizeDraft, setFontSizeDraft] = useState("14");
  const fontSizeRangeRef = useRef(null);
  const fontFamilyRangeRef = useRef(null);
  // Master saved-selection ref — updated whenever editor loses focus.
  // ALL toolbar formatting commands restore from this before executing.
  const savedSelRef = useRef(null);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed && editorRef.current?.contains(sel.anchorNode)) {
      savedSelRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const r = savedSelRef.current || fontSizeRangeRef.current || fontFamilyRangeRef.current;
    if (r) {
      try {
        editorRef.current?.focus();
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      } catch {}
    }
  };

  const [fontFamily, setFontFamily] = useState("");
  const applyLineHeight = (lh) => {
    const v = String(parseFloat(lh) || 1.5);
    setLineHeight(v);
    if (!editorRef.current) return;
    // Restore selection — the line-height select steals focus
    restoreSelection();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      // No selection — apply to the whole editor as a default
      editorRef.current.style.lineHeight = v;
      setDirty(true);
      return;
    }
    // Apply line-height to all block-level elements that intersect the selection
    const range = sel.getRangeAt(0);
    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_ELEMENT,
      { acceptNode: (node) => {
          const tag = node.tagName;
          if (/^(H[1-6]|P|DIV|BLOCKQUOTE|PRE|LI)$/.test(tag)) return NodeFilter.FILTER_ACCEPT;
          return NodeFilter.FILTER_SKIP;
        }
      }
    );
    let applied = false;
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (range.intersectsNode(node)) {
        node.style.lineHeight = v;
        applied = true;
      }
    }
    if (!applied) {
      // Cursor is inside a text node with no block parent found — apply to editor
      editorRef.current.style.lineHeight = v;
    }
    setDirty(true);
  };
  const applyFontSize = (px) => {
    setFontSizePx(px);
    if (!px) return;
    // Wrap the current selection in a span with explicit font-size — done
    // with Range.surroundContents to AVOID `document.execCommand("insertHTML",
    // …)` (which forced the scroll position to the top of the document and
    // dropped sibling formatting — recurring user bug).
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    if (!editorRef.current?.contains(sel.anchorNode)) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement("span");
    span.style.fontSize = `${parseInt(px) || 14}px`;
    try {
      // extractContents pulls the selection out into a fragment, which we
      // wrap in our span and re-insert at the original position.
      span.appendChild(range.extractContents());
      range.insertNode(span);
      // Restore the selection to the inserted span so subsequent style
      // changes apply to the same range.
      sel.removeAllRanges();
      const r = document.createRange();
      r.selectNodeContents(span);
      sel.addRange(r);
    } catch (err) {
      // Fallback to fontSize execCommand for selections that span multiple
      // block-level nodes (extractContents would split them).
      document.execCommand("styleWithCSS", false, true);
      document.execCommand("fontSize", false, "3");
      // Then upgrade the resulting <font size="3"> wrappers to inline px.
      editorRef.current?.querySelectorAll('font[size="3"]').forEach((f) => {
        const s = document.createElement("span");
        s.style.fontSize = `${parseInt(px) || 14}px`;
        while (f.firstChild) s.appendChild(f.firstChild);
        f.replaceWith(s);
      });
    }
    setDirty(true);
  };

  // Iter39: commit a draft font-size value. Restores the saved range
  // first so the wrap operation hits the user's original selection.
  const commitFontSize = () => {
    const px = (fontSizeDraft || "").trim();
    if (!px) { setFontSizeDraft(String(fontSizePx || 14)); return; }
    const num = parseInt(px, 10);
    if (!Number.isFinite(num) || num < 4 || num > 200) {
      setFontSizeDraft(String(fontSizePx || 14));
      return;
    }
    const r = fontSizeRangeRef.current;
    if (r) {
      // Restore saved selection and apply
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      } catch {}
      applyFontSize(String(num));
    } else if (savedSelRef.current) {
      // Use the master saved selection as fallback
      restoreSelection();
      applyFontSize(String(num));
    } else {
      // No selection saved — apply font size to the whole current block
      // so the user can at least set size for new typing at cursor position
      editorRef.current?.focus();
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        let block = sel.anchorNode;
        if (block?.nodeType === 3) block = block.parentNode;
        while (block && block !== editorRef.current && getComputedStyle(block).display === "inline") {
          block = block.parentNode;
        }
        if (block && block !== editorRef.current) {
          block.style.fontSize = `${num}px`;
          setDirty(true);
        }
      }
    }
    setFontSizePx(String(num));
    fontSizeRangeRef.current = null;
  };

  const applyFontFamily = (ff) => {
    setFontFamily(ff);
    if (!ff) return;
    // Always restore selection before applying — font picker portal steals focus
    restoreSelection();
    exec("fontName", ff);
    fontFamilyRangeRef.current = null;
  };

  // Image selection — click an inline image to "select" it (border + DEL
  // to remove). Backspace from adjacent line does NOT remove the image
  // because it lives inside `contenteditable="false"` wrapper (user
  // request: "Images should have to be clicked to be deleted, not using
  // backspace").
  const [selectedImgId, setSelectedImgId] = useState(null);
  useEffect(() => {
    const ed = editorRef.current;
    if (!ed) return;
    const onClick = (e) => {
      const wrap = e.target.closest?.(".cresc-doc-img-wrap");
      ed.querySelectorAll(".cresc-doc-img.cresc-img-selected").forEach((el) => el.classList.remove("cresc-img-selected"));
      if (!wrap) { setSelectedImgId(null); return; }
      const img = wrap.querySelector("img");
      img?.classList.add("cresc-img-selected");
      setSelectedImgId(wrap);
    };
    ed.addEventListener("click", onClick);
    return () => ed.removeEventListener("click", onClick);
  }, [active?.doc_id]);
  useEffect(() => {
    if (!selectedImgId) return;
    const onKey = (e) => {
      if ((e.key === "Delete" || e.key === "Backspace") && selectedImgId.parentNode) {
        e.preventDefault();
        selectedImgId.remove();
        setSelectedImgId(null);
        setDirty(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedImgId]);

  const insertChecklist = () => {
    const sel = window.getSelection();
    const txt = sel?.toString() || "";
    const html = (txt || "").split(/\n+/).map((l) => `<div class="cresc-checkitem"><input type="checkbox" /><span class="cresc-checkitem-text">${l}</span></div>`).join("") || `<div class="cresc-checkitem"><input type="checkbox" /><span class="cresc-checkitem-text"></span></div>`;
    exec("insertHTML", html);
  };

  // Editor key handler: Tab → indent (or 4 spaces); Enter on heading line
  // → next line resets to body; markdown auto-shortcuts ("* ", "1. ", "- ",
  // "_ ") at the very start of a line transform the line into the
  // matching list (user request: bullets, numbers, hyphens, checkbox).

  // ── Slash commands ────────────────────────────────────────────────────────
  const SLASH_COMMANDS = [
    { id: "h1",        icon: "H1",  label: "Heading 1",     cmd: () => exec("formatBlock", "h1") },
    { id: "h2",        icon: "H2",  label: "Heading 2",     cmd: () => exec("formatBlock", "h2") },
    { id: "bullet",    icon: "•",   label: "Bullet list",   cmd: () => exec("insertUnorderedList") },
    { id: "number",    icon: "1.",  label: "Numbered list", cmd: () => exec("insertOrderedList") },
    { id: "checklist", icon: "☑",  label: "Checklist",     cmd: () => exec("insertHTML", '<div class="cresc-checkitem"><input type="checkbox" />&nbsp;</div>') },
    { id: "divider",   icon: "—",   label: "Divider",       cmd: () => exec("insertHTML", "<hr />") },
    { id: "quote",     icon: "❝",   label: "Block quote",   cmd: () => exec("formatBlock", "blockquote") },
    { id: "code",      icon: "</>", label: "Code block",    cmd: () => exec("insertHTML", '<pre><code>&nbsp;</code></pre>') },
    { id: "table",     icon: "⊞",  label: "Table",         cmd: () => exec("insertHTML", '<table style="border-collapse:collapse;width:100%"><tr><td style="border:1px solid #2A2A2A;padding:6px 10px;min-width:80px">&nbsp;</td><td style="border:1px solid #2A2A2A;padding:6px 10px;min-width:80px">&nbsp;</td></tr><tr><td style="border:1px solid #2A2A2A;padding:6px 10px">&nbsp;</td><td style="border:1px solid #2A2A2A;padding:6px 10px">&nbsp;</td></tr></table>') },
    { id: "callout",   icon: "ℹ",  label: "Callout",       cmd: () => exec("insertHTML", '<div style="border-left:3px solid #5A90F5;padding:8px 12px;background:rgba(90,144,245,0.08);border-radius:4px;margin:4px 0">&nbsp;</div>') },
    { id: "2col",      icon: "⫿",  label: "2 columns",     cmd: () => exec("insertHTML", '<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:8px 0"><div style="border:1px solid #2A2A2A;border-radius:6px;padding:10px;min-height:60px">&nbsp;</div><div style="border:1px solid #2A2A2A;border-radius:6px;padding:10px;min-height:60px">&nbsp;</div></div><p>&nbsp;</p>') },
    { id: "3col",      icon: "⫿",  label: "3 columns",     cmd: () => exec("insertHTML", '<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin:8px 0"><div style="border:1px solid #2A2A2A;border-radius:6px;padding:10px;min-height:60px">&nbsp;</div><div style="border:1px solid #2A2A2A;border-radius:6px;padding:10px;min-height:60px">&nbsp;</div><div style="border:1px solid #2A2A2A;border-radius:6px;padding:10px;min-height:60px">&nbsp;</div></div><p>&nbsp;</p>') },
    { id: "cardgrid",  icon: "⊟",  label: "Card grid",     cmd: () => exec("insertHTML", '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:10px;margin:8px 0"><div style="border:1px solid #2A2A2A;border-radius:8px;padding:12px;background:#0A0A0A;min-height:80px">&nbsp;</div><div style="border:1px solid #2A2A2A;border-radius:8px;padding:12px;background:#0A0A0A;min-height:80px">&nbsp;</div><div style="border:1px solid #2A2A2A;border-radius:8px;padding:12px;background:#0A0A0A;min-height:80px">&nbsp;</div></div><p>&nbsp;</p>') },
    { id: "youtube",   icon: "▶",  label: "YouTube embed",  cmd: () => { const url = window.prompt("YouTube URL:"); if (!url) return; const id = url.match(/(?:v=|youtu\.be\/)([^&?]+)/)?.[1]; if (id) exec("insertHTML", `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:8px 0;border-radius:6px;border:1px solid #2A2A2A"><iframe src="https://www.youtube.com/embed/${id}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0" allowfullscreen></iframe></div>`); } },
    { id: "pagelink",  icon: "📄", label: "Link to page",   cmd: () => {
      const sel = window.getSelection();
      if (!sel?.rangeCount) return;
      const rng = sel.getRangeAt(0).cloneRange();
      rng.collapse(false);
      const rect = rng.getBoundingClientRect();
      setPageLinkMenu({ x: rect.left, y: rect.bottom + 6 });
    }},
    { id: "ai",        icon: "✦",  label: "AI Assistant",   cmd: () => openAiAssist() },
  ];

  const applySlashCommand = (item, slashNode, slashOffset, query) => {
    if (slashNode) {
      try {
        const sel = window.getSelection();
        const r = document.createRange();
        r.setStart(slashNode, slashOffset);
        r.setEnd(slashNode, slashOffset + 1 + (query?.length || 0));
        r.deleteContents();
        // Select the entire current block so formatBlock-style commands
        // convert the WHOLE line, not just insert at the cursor position
        const blockEl = (() => {
          const BLOCKS = new Set(["P","DIV","H1","H2","H3","H4","LI","BLOCKQUOTE","PRE"]);
          let el = slashNode.parentElement;
          while (el && el !== editorRef.current && !BLOCKS.has(el.tagName)) el = el.parentElement;
          return (el && el !== editorRef.current) ? el : slashNode.parentElement;
        })();
        const br = document.createRange();
        br.selectNodeContents(blockEl || slashNode.parentElement);
        sel?.removeAllRanges();
        sel?.addRange(br);
      } catch {}
    }
    item.cmd();
    setSlashMenu(null);
    setDirty(true);
    editorRef.current?.focus();
  };


  const prevDocIdRef = useRef(null);
  const multiPageRef = useRef(null);
  // Snapshot refs for content transfer across page-mode toggle
  const _pageModeHtml = useRef(null);

  // Auto-focus the editor whenever the active document changes OR component mounts.
  // This covers: switching between docs, returning from Inbox/Goals/etc.,
  // and first load. Only skips when it's a master/link doc (no text editor).
  useEffect(() => {
    if (!active || active.kind === "master" || active.kind === "link") return;
    if (!editorRef.current) return;
    // Fire on first mount (null → id) AND on every doc switch (old id → new id)
    if (prevDocIdRef.current !== active.doc_id) {
      prevDocIdRef.current = active.doc_id;
      setTimeout(() => {
        if (!editorRef.current) return;
        editorRef.current.contentEditable = "true";
        editorRef.current.focus();
        try {
          const rng = document.createRange();
          rng.selectNodeContents(editorRef.current);
          rng.collapse(false);
          window.getSelection()?.removeAllRanges();
          window.getSelection()?.addRange(rng);
        } catch {}
      }, 80);
    }
  }, [active?.doc_id]); // eslint-disable-line react-hooks/exhaustive-deps


  // When a "standard" type property changes, re-sort every line in this document
  // that was previously formatted with that standard (marked via data-formula-std)
  const reapplyStandardFormulas = useCallback((keyName, standardItems) => {
    if (!editorRef.current) return;
    const orderedLabels = standardItems.map(it => it.text).filter(Boolean);
    editorRef.current.querySelectorAll(`[data-formula-std="${keyName}"]`).forEach(el => {
      const lineText = el.textContent || "";
      const found = orderedLabels.filter(label =>
        lineText.toLowerCase().includes(label.toLowerCase())
      );
      if (found.length > 0) {
        el.innerHTML = found.join(", ");
      }
    });
    setDirty(true);
  }, []);


  // Ctrl+P / Cmd+P — print the document content only (not the whole app)
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        if (!editorRef.current || !active) return;
        e.preventDefault();
        const html = editorRef.current.innerHTML;
        const docName = active.name || "Document";
        const win = window.open("", "_blank", "width=800,height=900");
        if (!win) return;
        win.document.write(`<!DOCTYPE html><html><head>
          <title></title>
          <style>
            /* @page margin:0 removes the browser's header/footer area entirely */
            @page { margin: 0; size: auto; }
            html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            body { font-family: Georgia, serif; font-size: 12pt; line-height: 1.6;
                   margin: 1in; color: #000; background: #fff; }
            h1,h2,h3 { margin-top: 1.5em; }
            table { border-collapse: collapse; width: 100%; }
            td,th { border: 1px solid #ccc; padding: 6px 10px; }
            a { color: #1a0dab; }
            img { max-width: 100%; }
          </style>
        </head><body>${html}</body></html>`);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); win.close(); }, 400);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI writing assistant ──────────────────────────────────────────────────
  const openAiAssist = () => {
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const selectedText = sel.toString().trim();
    if (!selectedText) { toast.error("Select some text first"); return; }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    setAiAssistResult(null);
    setAiCustomPrompt("");
    setAiAssist({ x: rect.left, y: rect.bottom + 8, selectedText, range: range.cloneRange() });
  };

  const runAiAssist = async (action) => {
    if (!aiAssist) return;
    setAiAssistLoading(true);
    setAiAssistResult(null);
    try {
      const { data } = await api.post("/documents/ai-assist", {
        text: aiAssist.selectedText,
        action,
        custom_prompt: action === "custom" ? aiCustomPrompt : undefined,
      });
      setAiAssistResult(data.result);
    } catch {
      toast.error("AI assist failed");
    } finally {
      setAiAssistLoading(false);
    }
  };

  const applyAiResult = () => {
    if (!aiAssistResult) return;
    const sel = window.getSelection();
    if (aiAssist?.range) {
      sel.removeAllRanges();
      sel.addRange(aiAssist.range);
    }
    exec("insertText", aiAssistResult);
    setAiAssist(null);
    setAiAssistResult(null);
    setDirty(true);
  };

  const onEditorInput_slash = () => {
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== 3) { setSlashMenu(null); return; }
    const text = node.textContent.slice(0, range.startOffset);
    const slashIdx = text.lastIndexOf("/");
    if (slashIdx === -1) { setSlashMenu(null); return; }
    const query = text.slice(slashIdx + 1);
    if (/\s/.test(query) || query.length > 20) { setSlashMenu(null); return; }
    const filtered = SLASH_COMMANDS.filter(sc =>
      sc.label.toLowerCase().includes(query.toLowerCase()) || sc.id.includes(query.toLowerCase())
    );
    if (!filtered.length) { setSlashMenu(null); return; }
    try {
      const r2 = range.cloneRange(); r2.collapse(false);
      const rect = r2.getBoundingClientRect();
      setSlashMenu({ x: rect.left, y: rect.bottom + 6, query, items: filtered, node, slashOffset: slashIdx });
    } catch { setSlashMenu(null); }
  };

  const onEditorKeyDown = (e) => {
    // Slash menu keyboard nav
    if (slashMenu) {
      if (e.key === "Escape") { e.preventDefault(); setSlashMenu(null); return; }
      if (e.key === "Enter" && slashMenu.items.length > 0) { e.preventDefault(); applySlashCommand(slashMenu.items[0], slashMenu.node, slashMenu.slashOffset, slashMenu.query); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); return; }
    }

    // ── =keyname formula resolution ─────────────────────────────────────────
    if (e.key === " " || e.key === "Enter") {
      const sel = window.getSelection();
      // e.currentTarget is always the exact contentEditable div the keypress fired on.
      // Using it directly avoids DOM walk-up bugs in multi-page mode.
      const activeEditable = e.currentTarget;
      if (sel && sel.rangeCount > 0 && sel.isCollapsed && activeEditable) {
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        if (node.nodeType === 3) {
          const before = node.textContent.substring(0, range.startOffset);
          // On Enter: allow spaces in key (e.g. "=code format")
          // On Space: space-free only so mid-key spaces don't trigger prematurely
          const match = e.key === 'Enter'
            ? before.match(/=([a-zA-Z_][\w ]{0,60})$/)
            : before.match(/=([a-zA-Z_]\w*)$/);
          if (match) {
            const keyName = match[1].trim();
            try {
              const allProps = [
                ...(active?.properties || []),
                ...(docs || [])
                  .filter(d => d.doc_id !== active?.doc_id)
                  .flatMap(d => Array.isArray(d.properties) ? d.properties : [])
              ];
              const prop = allProps.find(p => p.key?.trim().toLowerCase() === keyName.toLowerCase() && !p._placeholder);
              if (prop && prop.value) {
                e.preventDefault();
                // Delete the =keyname token from the text
                const matchStart = range.startOffset - (keyName.length + 1);
                const delRange = document.createRange();
                delRange.setStart(node, matchStart);
                delRange.setEnd(node, range.startOffset);
                delRange.deleteContents();
                editorRef.current?.focus();

                // Find the nearest block-level element (the "line") —
                // node.parentElement could be the root editor div if there's no
                // block wrapper, which would select all text instead of one line.
                const BLOCK_TAGS = new Set(["P","DIV","H1","H2","H3","H4","LI","BLOCKQUOTE","PRE"]);
                let lineEl = node.parentElement;
                while (lineEl && lineEl !== activeEditable && !BLOCK_TAGS.has(lineEl.tagName)) {
                  lineEl = lineEl.parentElement;
                }
                if (!lineEl || lineEl === activeEditable) lineEl = node.parentElement || activeEditable;

                const applyToLine = (cmd, val) => {
                  const lineRange = document.createRange();
                  lineRange.selectNodeContents(lineEl);
                  const s = window.getSelection();
                  s?.removeAllRanges();
                  s?.addRange(lineRange);
                  document.execCommand(cmd, false, val);
                  lineRange.collapse(false);
                  s?.removeAllRanges();
                  s?.addRange(lineRange);
                };

                if (prop.type === "color") {
                  applyToLine("foreColor", prop.value);

                } else if (prop.type === "font") {
                  applyToLine("fontName", prop.value);

                } else if (prop.type === "standard") {
                  // Standard type: reorder LINES across the document to match
                  // the ordering defined in the standard property list.
                  // Each line whose full text equals a standard item gets sorted;
                  // the formula line (=keyname) is then deleted.
                  try {
                    const rawVal = prop.value;
                    const standardItems = Array.isArray(rawVal) ? rawVal
                      : (typeof rawVal === 'string' ? JSON.parse(rawVal) : []);
                    const orderedLabels = standardItems.map(it => (it.text || '').trim()).filter(Boolean);
                    if (!orderedLabels.length) { /* nothing to sort */ }

                    // Use the page the user is on (activeEditable), not just page 0
                    const scanRoot = activeEditable || editorRef.current;
                    const allBlocks = [...(scanRoot?.children || [])];

                    // Map each standard label to the first block in the doc whose
                    // text exactly matches it (case-insensitive)
                    const matchedEls = new Set();
                    const matchMap = new Map();
                    orderedLabels.forEach(label => {
                      const lc = label.toLowerCase();
                      const found = allBlocks.find(el =>
                        !matchedEls.has(el) && el !== lineEl &&
                        el.textContent?.trim().toLowerCase() === lc
                      );
                      if (found) { matchMap.set(label, found); matchedEls.add(found); }
                    });

                    if (matchMap.size === 0) {
                      // Property found but no lines matched — inform the user
                      toast.info(`No lines matched the "${keyName}" standard. Write each item on its own line first.`);
                    } else if (matchMap.size > 0) {
                      // Find where the first matched block sits so we can re-insert there
                      const firstMatch = allBlocks.find(el => matchedEls.has(el));
                      const parent = scanRoot;
                      // Move blocks into standard order before the firstMatch anchor
                      orderedLabels.forEach(label => {
                        const el = matchMap.get(label);
                        if (el) parent.insertBefore(el, firstMatch);
                      });
                      lineEl.remove(); // delete the =keyname trigger line
                      // Place cursor after the last sorted block
                      const lastLabel = [...orderedLabels].reverse().find(l => matchMap.has(l));
                      const lastEl = lastLabel ? matchMap.get(lastLabel) : null;
                      if (lastEl) {
                        const r = document.createRange();
                        r.selectNodeContents(lastEl); r.collapse(false);
                        window.getSelection()?.removeAllRanges();
                        window.getSelection()?.addRange(r);
                      }
                    }
                  } catch {}

                } else {
                  // Text / number / url: insert value at cursor
                  const curSel = window.getSelection();
                  if (curSel && curSel.rangeCount > 0) {
                    const nr = curSel.getRangeAt(0);
                    const valNode = document.createTextNode(prop.value + " ");
                    nr.insertNode(valNode);
                    nr.setStartAfter(valNode);
                    nr.collapse(true);
                    curSel.removeAllRanges();
                    curSel.addRange(nr);
                  }
                }
                setDirty(true);
                return;
              }
            } catch (formulaErr) {
              // silent — let keypress continue normally
            }
          }
        }
      }
    }

    // Wrap selected text with matching pairs when user types opening char
    const WRAP_PAIRS = { '"': ['"', '"'], "'": ["'", "'"], "(": ["(", ")"], "[": ["[", "]"], "{": ["{", "}"] };
    if (WRAP_PAIRS[e.key]) {
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
        e.preventDefault();
        const [open, close] = WRAP_PAIRS[e.key];
        const range = sel.getRangeAt(0);
        const selected = range.extractContents();
        const span = document.createElement("span");
        span.appendChild(document.createTextNode(open));
        span.appendChild(selected);
        span.appendChild(document.createTextNode(close));
        range.insertNode(span);
        // Move cursor to after the closing char
        range.setStartAfter(span);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        setDirty(true);
        return;
      }
    }
    // Backspace after a shorthand chip → remove chip, restore trigger as plain text
    if (e.key === "Backspace") {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && sel.isCollapsed) {
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        let prev = null;
        if (node.nodeType === 3 && range.startOffset === 0) {
          prev = node.previousSibling;
        } else if (node.nodeType === 1 && range.startOffset > 0) {
          prev = node.childNodes[range.startOffset - 1] || null;
        }
        if (prev?.classList?.contains("cresc-shorthand-chip")) {
          e.preventDefault();
          const trigger = prev.dataset.trigger || prev.textContent;
          const textNode = document.createTextNode(trigger);
          prev.replaceWith(textNode);
          const r2 = document.createRange();
          r2.setStartAfter(textNode);
          r2.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r2);
          setDirty(true);
          return;
        }
      }
    }
    if (e.key === "Backspace") {
      if (r.startOffset !== 0) return;
      // Check if we're at the start of a list item (existing unwrap logic)
      let node = r.startContainer;
      if (node.nodeType === 3 && r.startOffset > 0) return;
      const li = node.nodeType === 3 ? node.parentElement?.closest("li") : node.closest?.("li");
      if (!li) {
        // Check if heading would become empty — prevent it from collapsing to <div>
        // by inserting a placeholder <br> instead of removing the heading tag.
        let hNode = node.nodeType === 3 ? node.parentElement : node;
        while (hNode && hNode !== editorRef.current && !/^H[1-3]$/.test(hNode.tagName)) {
          hNode = hNode.parentElement;
        }
        if (hNode && /^H[1-3]$/.test(hNode.tagName)) {
          const text = hNode.textContent || "";
          if (text.length <= 1) {
            // Don't delete — clear content but keep the heading tag
            e.preventDefault();
            hNode.innerHTML = "<br>";
            const newRange = document.createRange();
            newRange.setStart(hNode, 0);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
            setDirty(true);
            return;
          }
        }
        return;
      }
      const list = li.parentElement;
      if (!list || !["UL", "OL"].includes(list.tagName)) return;
      e.preventDefault();
      const p = document.createElement("p");
      while (li.firstChild) p.appendChild(li.firstChild);
      if (!p.innerHTML.trim()) p.innerHTML = "<br>";
      if (li === list.firstElementChild && li === list.lastElementChild) {
        list.replaceWith(p);
      } else if (li === list.firstElementChild) {
        list.parentNode.insertBefore(p, list);
        li.remove();
      } else {
        list.after(p);
        li.remove();
      }
      const newRange = document.createRange();
      newRange.setStart(p, 0);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
      setDirty(true);
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      const sel = window.getSelection();
      let node = sel?.anchorNode;
      while (node && node !== editorRef.current && (node.nodeType !== 1 || !/^(LI|UL|OL)$/.test(node.tagName))) node = node.parentNode;
      if (node && /^(LI|UL|OL)$/.test(node.tagName)) {
        document.execCommand(e.shiftKey ? "outdent" : "indent");
      } else {
        document.execCommand("insertText", false, "    ");
      }
      setDirty(true);
      return;
    }
    if (e.key === "Enter") {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      let node = sel.anchorNode;
      if (node && node.nodeType === 3) node = node.parentNode;

      // Case 1: cursor is inside a heading that is itself inside a list item.
      // The browser will create a new <li> — we want a plain <p> instead.
      const liParent = node?.closest?.("li");
      const headingInLi = liParent
        ? [...liParent.querySelectorAll("h1,h2,h3")].some(h => h.contains(sel.anchorNode))
        : false;

      // Case 2: cursor is directly inside a heading (normal case).
      let headingNode = null;
      let tmp = node;
      while (tmp && tmp !== editorRef.current && !/^H[1-3]$/.test(tmp.tagName)) tmp = tmp.parentNode;
      if (tmp && /^H[1-3]$/.test(tmp.tagName)) headingNode = tmp;

      if (headingNode || headingInLi) {
        e.preventDefault();
        // Instead of letting the browser decide, manually create a new <p> after the heading
        const sel2 = sel;
        const range = sel2.getRangeAt(0);
        // Split heading content at cursor
        const afterFrag = range.cloneContents(); // content after cursor (empty if at end)
        range.deleteContents(); // remove what's after cursor in heading
        // Create new <p> for the next line
        const newP = document.createElement("p");
        if (afterFrag && afterFrag.childNodes.length > 0) {
          newP.appendChild(afterFrag);
        } else {
          newP.innerHTML = "<br>";
        }
        // Insert after the current heading/li
        const insertAfter = headingNode || liParent;
        insertAfter.after(newP);
        // Move cursor to start of new paragraph
        const newRange = document.createRange();
        newRange.setStart(newP, 0);
        newRange.collapse(true);
        sel2.removeAllRanges();
        sel2.addRange(newRange);
        setDirty(true);
        return;
      }
    }
    if (e.key === " ") {
      // Markdown autolist — only when the typed marker IS the entire
      // beginning of the current line.
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const r = sel.getRangeAt(0);
      const node = r.startContainer;
      if (node.nodeType !== 3) return;
      const text = node.textContent || "";
      const before = text.slice(0, r.startOffset);
      const trigger = before.trim();
      if (before.replace(/\s+$/, "") !== trigger) return;
      let kind = null;
      if (trigger === "*") kind = "ul";
      else if (trigger === "1." || trigger === "1") kind = "ol";
      else if (trigger === "-") kind = "ul-dash";
      else if (trigger === "_") kind = "checklist";
      else return;
      e.preventDefault();

      // If the cursor is already inside a list item, CONVERT the list type
      // instead of creating a nested list. This prevents two list types on
      // the same line.
      const existingLi = node.parentElement?.closest("li");
      const existingCheckitem = node.parentElement?.closest(".cresc-checkitem");
      if (existingLi || existingCheckitem) {
        node.textContent = text.slice(r.startOffset); // strip trigger char
        if (kind === "checklist") {
          const li = existingLi;
          const parentList = li?.closest("ul, ol");
          const div = document.createElement("div");
          div.className = "cresc-checkitem";
          div.innerHTML = `<input type="checkbox" /><span class="cresc-checkitem-text"> ${li?.textContent || ""}</span>`;
          if (parentList && parentList.children.length === 1) {
            parentList.replaceWith(div);
          } else if (li) {
            li.parentNode?.insertBefore(div, li);
            li.remove();
          }
        } else if (existingCheckitem) {
          // Convert checkitem to regular list
          const newLi = document.createElement("li");
          newLi.textContent = existingCheckitem.querySelector(".cresc-checkitem-text")?.textContent?.trim() || "";
          const newList = document.createElement(kind === "ol" ? "ol" : "ul");
          newList.appendChild(newLi);
          existingCheckitem.replaceWith(newList);
        } else if (existingLi) {
          const parentList = existingLi.closest("ul, ol");
          if (parentList) {
            const newList = document.createElement(kind === "ol" ? "ol" : "ul");
            while (parentList.firstChild) newList.appendChild(parentList.firstChild);
            if (kind === "ul-dash") newList.style.listStyleType = "square";
            parentList.replaceWith(newList);
          }
        }
        setDirty(true);
        return;
      }
      // Iter35 fix for recurring "list-under-text turns text above into
      // bullet" bug. Old approach (`formatBlock=p` then
      // `insertUnorderedList`) acted on the WHOLE parent — so when the
      // line above wasn't already wrapped in its own <p>, it got
      // absorbed too. New approach:
      //   1. Strip the trigger char from the current text node.
      //   2. Find or create a dedicated block element holding ONLY the
      //      residual current-line text, leaving sibling text alone.
      //   3. Apply the list to JUST that block via direct DOM op.
      node.textContent = text.slice(r.startOffset);
      // Walk up to find the nearest block (paragraph-level) ancestor.
      let block = node.parentNode;
      while (block && block !== editorRef.current && getComputedStyle(block).display === "inline") {
        block = block.parentNode;
      }
      // If the residual line is sitting directly inside the editor (no
      // block wrapper) OR shares a block with prior text, wrap JUST the
      // current line in a fresh <p> so the list conversion is local.
      if (block === editorRef.current || !["P", "DIV"].includes(block?.tagName)) {
        const newP = document.createElement("p");
        // Move only the current text node (the line we're triggering on).
        node.parentNode.insertBefore(newP, node);
        newP.appendChild(node);
        block = newP;
      }
      // Build the list with one <li> wrapping the current line's content.
      const list = document.createElement(kind === "ol" ? "ol" : "ul");
      const li = document.createElement("li");
      while (block.firstChild) li.appendChild(block.firstChild);
      list.appendChild(li);
      block.replaceWith(list);
      if (kind === "ul-dash") list.style.listStyleType = "square";
      // Restore caret to the start of the new <li>.
      const newRange = document.createRange();
      const target = li.firstChild || li;
      newRange.setStart(target, 0);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
      if (kind === "checklist") {
        // For checklists we still rely on the existing helper which
        // wraps in the structured DIV-based checklist (preserves
        // indeterminate state etc.). Fall back to the prior path here.
        // Move caret into the newly-created li first so insertChecklist
        // operates within the LI.
        insertChecklist();
      }
      setDirty(true);
    }
  };

  // Sync the toolbar fields with whatever the cursor is currently inside.
  // Recurring user bug: "highlighting a heading (size 24), then selecting
  // body text (size 14), the size box still shows 24". We now poll
  // selectionchange and read computed style for the focused selection's
  // anchor so the toolbar always reflects the current text.
  useEffect(() => {
    const onSel = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      let node = sel.anchorNode;
      if (!node || !editorRef.current?.contains(node)) return;
      if (node.nodeType === 3) node = node.parentNode;
      const cs = getComputedStyle(node);
      const px = parseInt(cs.fontSize || "14");
      const lh = cs.lineHeight && cs.lineHeight !== "normal" ? cs.lineHeight : "";
      // Only update if the value is meaningfully different — avoids re-
      // rendering on every keystroke.
      setFontSizePx((prev) => (prev !== String(px) ? String(px) : prev));
      if (lh) {
        // Convert px line-height back to a fraction-relative number when
        // possible (e.g. 24px @ 16px font = 1.5).
        let lhVal = lh;
        if (lh.endsWith("px")) {
          const ratio = parseFloat(lh) / px;
          lhVal = String(Math.round(ratio * 100) / 100);
        }
        setLineHeight((prev) => (prev !== lhVal ? lhVal : prev));
      }
      const family = (cs.fontFamily || "").split(",")[0]?.replace(/['"]/g, "").trim();
      if (family) setFontFamily((prev) => (prev !== family ? family : prev));
      updateCurrentBlock();
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, [active?.doc_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Insert row/column handling for in-doc tables.
  const [activeCell, setActiveCell] = useState(null);
  useEffect(() => {
    const ed = editorRef.current;
    if (!ed) return;
    const onClick = (e) => {
      const td = e.target.closest?.("td");
      if (!td || !td.closest?.("table.cresc-table")) { setActiveCell(null); return; }
      const tr = td.parentElement;
      const table = tr.parentElement;
      const row = Array.from(table.children).indexOf(tr);
      const col = Array.from(tr.children).indexOf(td);
      setActiveCell({ table, row, col });
    };
    ed.addEventListener("click", onClick);
    return () => ed.removeEventListener("click", onClick);
  }, [active?.doc_id]);

  const addTableRow = () => {
    if (!activeCell) return;
    const { table, row } = activeCell;
    const cols = table.rows[0]?.cells.length || 2;
    const newTr = table.insertRow(row + 1);
    for (let i = 0; i < cols; i++) {
      const td = newTr.insertCell(i);
      td.contentEditable = "true";
      td.style.cssText = "border:1px solid #2A2A2A;padding:6px 10px;min-width:80px;";
      td.innerHTML = "&nbsp;";
    }
    setDirty(true);
  };
  const addTableCol = () => {
    if (!activeCell) return;
    const { table, col } = activeCell;
    for (const tr of table.rows) {
      const td = tr.insertCell(col + 1);
      td.contentEditable = "true";
      td.style.cssText = "border:1px solid #2A2A2A;padding:6px 10px;min-width:80px;";
      td.innerHTML = "&nbsp;";
    }
    setDirty(true);
  };

  // Microsoft-Word-style right-click context menu over the editor.
  const [ctx, setCtx] = useState(null); // { x, y, hasSelection }
  const ctxSavedRangeRef = useRef(null);
  const onEditorContextMenu = (e) => {
    e.preventDefault();
    const sel = window.getSelection();
    const text = sel?.toString() || "";
    // Cache the selection range so the convert-to handlers can restore it
    // (clicking on a popover button kills the contenteditable selection,
    // user bug: "convert-to options in the right-click menu don't work").
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      ctxSavedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
    setCtx({ x: e.clientX, y: e.clientY, hasSelection: text.length > 0 });
  };
  const restoreCtxSelection = () => {
    if (!ctxSavedRangeRef.current) return;
    editorRef.current?.focus();
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(ctxSavedRangeRef.current);
  };
  useEffect(() => {
    if (!ctx) return;
    const close = () => setCtx(null);
    // Only close on scrolls that happen OUTSIDE the ctx menu container.
    // Internal scrolls inside nested ThemedSelect dropdowns (e.g. the
    // Convert dropdown) used to bubble up through capture and close
    // the menu — iter28 fix.
    const onScroll = (e) => {
      const menu = document.querySelector('[data-cresc-doc-ctx-menu="1"]');
      if (menu && e.target && menu.contains(e.target)) return;
      // Also ignore scrolls inside any portaled ThemedSelect panel —
      // the new (iter32) ThemedSelect renders its dropdown via React
      // portal at <body>, so scrolling the dropdown's option list has
      // a target OUTSIDE the ctx menu's DOM subtree.
      const panel = e.target?.closest && e.target.closest('[data-themed-select-panel="1"]');
      if (panel) return;
      setCtx(null);
    };
    window.addEventListener("click", close);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [ctx]);

  // Share dialog with full Workspace-share parity — modes private / public /
  // invite / secured (user request: "Document share modes parity with
  // workspace shares"). Source-of-truth is the per-doc PUT /share endpoint
  // we just added on the backend.
  const [shareOpen, setShareOpen] = useState(false);
  const [docFilter, setDocFilter] = useState("all");
  const [readMode, setReadMode] = useState(false);
  const [showToc, setShowToc] = useState(false);
  const [pageMode, setPageMode] = useState(() => {
    try { return localStorage.getItem("crescendo:docPageMode") === "page"; } catch { return false; }
  });
  const [pageOpts, setPageOpts] = useState(() => {
    try { return { color:"#1e1e1e", textColor:"auto", size:"letter", margins:"normal", seamless:false, glass:false,
                   ...JSON.parse(localStorage.getItem("crescendo:pageOpts")||"{}") }; } catch { return {color:"#1e1e1e",textColor:"auto",size:"letter",margins:"normal",seamless:false,glass:false}; }
  });
  const savePageOpts = (next) => {
    setPageOpts(next);
    try { localStorage.setItem("crescendo:pageOpts", JSON.stringify(next)); } catch {}
  };
  const togglePageMode = () => {
    // Capture content BEFORE the state change so we have it after re-render
    const snapshot = editorRef.current?.innerHTML || "";
    setPageMode(v => {
      const next = !v;
      try { localStorage.setItem("crescendo:docPageMode", next ? "page" : "pageless"); } catch {}
      // Store snapshot on the ref so the scheduled callback can access it
      _pageModeHtml.current = snapshot;
      return next;
    });
    // After React re-renders with the new mode, seed the destination editor
    setTimeout(() => {
      const html = _pageModeHtml.current;
      _pageModeHtml.current = null;
      if (!html) return;
      // Detect current mode after re-render via multiPageRef presence
      if (multiPageRef.current && multiPageRef.current.pageRefs[0]) {
        multiPageRef.current.pageRefs[0].innerHTML = html;
        multiPageRef.current.rebalance(0);
      } else if (editorRef.current) {
        editorRef.current.innerHTML = html;
        editorRef.current.contentEditable = "true";
        setDirty(true);
      }
    }, 80);
  };
  const [pageOptsOpen, setPageOptsOpen] = useState(false);
  // Derive text color: "auto" = flip based on bg luminance
  const pageTextColor = (() => {
    if (pageOpts.textColor && pageOpts.textColor !== "auto") return pageOpts.textColor;
    const hex = (pageOpts.color||"#ffffff").replace("#","");
    const r=parseInt(hex.slice(0,2),16)/255, g=parseInt(hex.slice(2,4),16)/255, b=parseInt(hex.slice(4,6),16)/255;
    const lum = 0.2126*r + 0.7152*g + 0.0722*b;
    return lum > 0.5 ? "#111111" : "#EDEDED";
  })();
  const PAGE_SIZES = { letter:{w:816,h:1056}, a4:{w:794,h:1123}, legal:{w:816,h:1344}, a5:{w:560,h:794} };
  const PAGE_MARGINS = { none:0, narrow:36, normal:72, wide:108 };
  const [slashMenu, setSlashMenu] = useState(null); // {x,y,query,items}
  const [pageLinkMenu, setPageLinkMenu] = useState(null); // {x,y} — opened from slash /link command
  const [aiAssist, setAiAssist] = useState(null); // {x,y,selectedText,selRange} — inline AI popup
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiAssistResult, setAiAssistResult] = useState(null);
  const [aiCustomPrompt, setAiCustomPrompt] = useState("");
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummarizing, setAiSummarizing] = useState(false);
  const [shorthandPreview, setShorthandPreview] = useState(null); // {trigger, expansion, partial}
  const [shareMode, setShareMode] = useState("private");
  const [sharePassword, setSharePassword] = useState("");
  const [shareInvitees, setShareInvitees] = useState([]); // [{email, role}]
  const [pillDraft, setPillDraft] = useState("");
  const [shareSaving, setShareSaving] = useState(false);
  const [shareToken, setShareToken] = useState("");
  useEffect(() => {
    if (!active) return;
    setShareMode(active.share_mode || "private");
    setShareInvitees((active.share_invitees || []).map((inv) =>
      typeof inv === "string" ? { email: inv, role: "viewer" } : { email: inv.email, role: inv.role || "viewer" }
    ));
    setShareToken(active.share_token || "");
    setSharePassword("");
  }, [active?.doc_id, active?.share_mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const shareUrl = useMemo(() => {
    if (!active || !shareToken) return "";
    return `${window.location.origin}/share/doc/${shareToken}`;
  }, [active, shareToken]);
  const saveShare = async () => {
    if (!active) return;
    setShareSaving(true);
    try {
      const body = { mode: shareMode };
      if (shareMode === "invite" || shareMode === "secured") body.invitees = shareInvitees.map((x) => ({ email: x.email, role: x.role || "viewer" }));
      if (shareMode === "secured" && sharePassword) body.password = sharePassword;
      const { data } = await api.put(`/documents/${active.doc_id}/share`, body);
      if (data.share_token) setShareToken(data.share_token);
      else if (shareMode === "private") setShareToken("");
      toast.success("Share settings saved");
      window.dispatchEvent(new CustomEvent("crescendo:docs-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
    setShareSaving(false);
  };
  const addPill = () => {
    const v = pillDraft.trim().replace(/[, ]+$/, "");
    if (!v || !/.@./.test(v)) return;
    if (shareInvitees.find((x) => x.email.toLowerCase() === v.toLowerCase())) { setPillDraft(""); return; }
    setShareInvitees((arr) => [...arr, { email: v, role: "viewer" }]);
    setPillDraft("");
  };

  return (
    <div className="h-full w-full flex bg-[#0A0A0A] text-[#EDEDED]" data-testid="documents-view" data-cresc-docs-root>
      <BulletShapePicker />
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Row 1: action buttons — own row above tabs */}
        <div className="border-b border-[#1a1a1a] flex items-center justify-between px-4 py-2 bg-[#0D0D0D]">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#3F3F46] uppercase tracking-wider font-semibold">
              {active ? active.name : "Documents"}
            </span>
            {!active && (
              <select
                value={docFilter}
                onChange={e => setDocFilter(e.target.value)}
                className="text-[10px] text-[#52525B] bg-transparent border border-[#2A2A2A] rounded px-1.5 py-0.5 outline-none cursor-pointer hover:border-[#3F3F46]"
                data-testid="docs-filter"
              >
                <option value="all">All Documents</option>
                <option value="owned">Owned by me</option>
                <option value="shared">Shared with me</option>
              </select>
            )}
          </div>
          {active && (
            <div className="flex items-center gap-1.5">
              <button onClick={() => setCommentsOpen((v) => !v)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded border text-[11px] ${commentsOpen ? "border-[#5A90F5] bg-[#5A90F5]/10 text-[#5A90F5]" : "border-[#2A2A2A] bg-[#141414] text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1f1f1f]"}`}
                data-testid="docs-comments-toggle">
                <MessageSquare className="w-3 h-3" /> Comments
              </button>
              <button onClick={() => setShareOpen(true)}
                className="flex items-center gap-1 px-2.5 py-1 rounded border border-[#2A2A2A] bg-[#141414] text-[#A1A1AA] text-[11px] hover:text-[#EDEDED] hover:bg-[#1f1f1f]"
                data-testid="docs-share-btn">
                <Share2 className="w-3 h-3" /> Share
              </button>

              <button onClick={save}
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${dirty ? "bg-[#EDEDED] text-[#0A0A0A] hover:bg-white" : "bg-[#1a1a1a] text-[#A1A1AA] hover:bg-[#222]"}`}
                data-testid="docs-save-btn"
                title={dirty ? "Unsaved changes" : lastSavedAt ? `Saved ${lastSavedAt.toLocaleTimeString()}` : "Saved"}>
                <Save className="w-3 h-3" />{dirty ? "Save" : "Saved"}
              </button>
              {/* Word count + reading time — computed from editor text content */}
              {editorRef.current && (() => {
                const text = editorRef.current.innerText || "";
                const words = text.trim().split(/\s+/).filter(Boolean).length;
                const mins = Math.max(1, Math.round(words / 200));
                if (words < 10) return null;
                return (
                  <span className="text-[10px] text-[#3A3A3A] select-none whitespace-nowrap">
                    {words.toLocaleString()} words · {mins} min read
                  </span>
                );
              })()}
            </div>
          )}
        </div>
        {/* File / Text / Insert / View navbar — shown BELOW the doc tabs, above the formatting toolbar */}
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-[#A1A1AA] text-sm">
            Select or create a document.
          </div>
        ) : (
          <>
            {/* Google-Docs-style sub-tab rail — appears when this document
                has internal tabs. */}
            {Array.isArray(active.tabs) && active.tabs.length > 0 && (
              <div className="border-b border-[#2A2A2A] flex items-center gap-1 px-3 py-1 overflow-x-auto bg-[#0D0D0D]" data-testid="docs-subtab-bar">
                <button
                  onClick={() => setActiveSubtabId(null)}
                  className={`text-[11px] px-2 py-1 rounded ${activeSubtabId === null ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#A1A1AA] hover:bg-[#1a1a1a]"}`}
                  data-testid="docs-subtab-main"
                >
                  Main
                </button>
                {active.tabs.map((t) => (
                  <button
                    key={t.tab_id}
                    onClick={() => setActiveSubtabId(t.tab_id)}
                    className={`group text-[11px] px-2 py-1 rounded flex items-center gap-1 ${activeSubtabId === t.tab_id ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#A1A1AA] hover:bg-[#1a1a1a]"}`}
                    data-testid={`docs-subtab-${t.tab_id}`}
                  >
                    {t.name}
                    <span
                      onClick={async (e) => {
                        e.stopPropagation();
                        const next = active.tabs.filter((x) => x.tab_id !== t.tab_id);
                        try {
                          await update(active.doc_id, {
                            name: active.name, kind: active.kind, body: active.body || "",
                            mime_type: active.mime_type, workspace_id: active.workspace_id,
                            parent_folder_id: active.parent_folder_id, tabs: next,
                          });
                          if (activeSubtabId === t.tab_id) setActiveSubtabId(null);
                          window.dispatchEvent(new CustomEvent("crescendo:docs-changed"));
                        } catch {}
                      }}
                      className="opacity-0 group-hover:opacity-60 hover:!opacity-100 px-1 rounded hover:bg-[#2a2a2a]"
                      title="Remove tab"
                    >×</span>
                  </button>
                ))}
              </div>
            )}
            {active.kind === "master" && !activeSubtabId ? (
              <MasterDocPropsWithCtx
                doc={active}
                onSave={async (props) => {
                  // Re-apply any standard formulas that changed
                  props.forEach(prop => {
                    if (prop.type === "standard" && prop.value) {
                      try {
                        const items = JSON.parse(prop.value);
                        reapplyStandardFormulas(prop.key, items);
                      } catch {}
                    }
                  });
                  patchDoc(active.doc_id, { properties: props });
                  try {
                    await api.put(`/documents/${active.doc_id}`, { properties: props });
                  } catch (err) {
                    console.warn("[MasterDoc] property save failed:", err?.response?.status, err?.message);
                    toast?.error("Property save failed — changes may be lost on refresh");
                  }
                }}
              />
            ) : active.kind === "link" ? (
              <div className="flex-1 p-6">
                <label className="meta-label block mb-2">URL</label>
                <input
                  value={linkUrl}
                  onChange={(e) => { setLinkUrl(e.target.value); setDirty(true); }}
                  placeholder="https://…"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-sm font-mono"
                  data-testid="docs-link-input"
                />
                {linkUrl && /^https?:\/\//i.test(linkUrl) && (
                  <a href={linkUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-[#5A90F5] hover:underline text-xs">
                    Open → {linkUrl}
                  </a>
                )}
              </div>
            ) : activeSubtabId && (() => {
              const tab = (active.tabs || []).find((t) => t.tab_id === activeSubtabId);
              const isEmpty = !tab?.body || tab.body.replace(/<[^>]*>/g, "").trim().length === 0;
              return isEmpty;
            })() ? (
              // GitHub-style list view when a tab is opened with no body —
              // organizational navigation (user request: "opening the tab
              // should just show a list of all items underneath it for
              // navigation … look like GitHub with the site theme").
              <div className="flex-1 overflow-y-auto p-6 max-w-3xl w-full self-center" data-testid="docs-tab-list-view">
                <div className="flex items-center gap-2 mb-3 text-xs text-[#A1A1AA]">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="text-[#EDEDED]">{active.name}</span>
                  <span>›</span>
                  <span className="text-[#EDEDED]">{(active.tabs || []).find((t) => t.tab_id === activeSubtabId)?.name || "Tab"}</span>
                </div>
                <div className="border border-[#2A2A2A] rounded-md overflow-hidden">
                  <div className="bg-[#1a1a1a] px-3 py-1.5 border-b border-[#2A2A2A] text-[10px] uppercase tracking-wider text-[#A1A1AA] flex items-center justify-between">
                    <span>Items</span>
                    <span>Last edited</span>
                  </div>
                  {/* For now: list the OTHER tabs in this document so users
                      can navigate between them. When parent_doc_id support
                      lands for docs/folders we can include nested items
                      here too. */}
                  {(active.tabs || []).filter((t) => t.tab_id !== activeSubtabId).map((t) => (
                    <button
                      key={t.tab_id}
                      onClick={() => setActiveSubtabId(t.tab_id)}
                      className="w-full px-3 py-2 flex items-center gap-2 text-left text-xs text-[#EDEDED] hover:bg-[#151515] border-b border-[#2A2A2A] last:border-b-0"
                      data-testid={`docs-tab-list-item-${t.tab_id}`}
                    >
                      <FileEdit className="w-3.5 h-3.5 text-[#5A90F5]" />
                      <span className="flex-1 truncate">{t.name}</span>
                      <span className="text-[10px] text-[#A1A1AA]">tab</span>
                    </button>
                  ))}
                  <button
                    onClick={() => setActiveSubtabId(null)}
                    className="w-full px-3 py-2 flex items-center gap-2 text-left text-xs text-[#EDEDED] hover:bg-[#151515]"
                    data-testid="docs-tab-list-item-main"
                  >
                    <FileText className="w-3.5 h-3.5 text-[#A1A1AA]" />
                    <span className="flex-1 truncate">Main document</span>
                    <span className="text-[10px] text-[#A1A1AA]">document</span>
                  </button>
                </div>
                <p className="mt-3 text-[11px] text-[#A1A1AA]">
                  Tabs are organizational only — they don't store content. Add a tab via the
                  + button next to this tab in the sidebar, or click an item above to navigate.
                </p>
              </div>
            ) : (
              <>
                <div className="border-b border-[#2A2A2A] bg-[#0D0D0D]" data-testid="docs-toolbar">
                  {/* Row 1: Tab labels */}
                  <div className="flex items-center px-3 pt-1 gap-0">
                    {["file","text","insert","view"].map(tab => (
                      <button key={tab} onClick={() => setActiveMenuTab(tab)}
                        className={`px-3 py-1.5 text-[11px] capitalize border-b-2 transition-colors -mb-px ${
                          activeMenuTab === tab
                            ? "border-[#5A90F5] text-[#EDEDED]"
                            : "border-transparent text-[#52525B] hover:text-[#A1A1AA]"
                        }`}
                        data-testid={`docs-tab-${tab}`}
                      >{tab}</button>
                    ))}
                    {/* Ink mode — write directly on document */}
                    <button onClick={() => setInkMode(v => !v)}
                      className={`ml-auto p-1.5 rounded transition-colors ${inkMode ? "bg-[#5A90F5]/20 text-[#5A90F5]" : "text-[#52525B] hover:text-[#EDEDED] hover:bg-[#1a1a1a]"}`}
                      title="Write on document">
                      <Pen className="w-3.5 h-3.5"/>
                    </button>
                  </div>

                  {/* Row 2: Icon-only controls based on active tab */}
                  <div className="flex items-center px-2 py-1 gap-0.5 min-h-[32px]">

                    {/* ── File ── */}
                    {activeMenuTab === "file" && <>
                      <button onClick={save} title={dirty ? "Save" : "Saved"} className={`p-1.5 rounded hover:bg-[#1a1a1a] transition-colors ${dirty ? "text-[#EDEDED]" : "text-[#52525B]"}`}>
                        <Save className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setPageOptsOpen(v => !v)} title="Page Options"
                        className={`p-1.5 rounded hover:bg-[#1a1a1a] transition-colors ${pageOptsOpen ? "text-[#5A90F5]" : "text-[#A1A1AA] hover:text-[#EDEDED]"}`}>
                        <Layout className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setShareOpen(true)} title="Share" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <Share2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => {
                        const blob = new Blob([`<!doctype html><meta charset='utf-8'><title>${active?.name || "Document"}</title><body>${editorRef.current?.innerHTML || ""}</body>`], { type: "text/html" });
                        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${active?.name || "document"}.html`; a.click(); URL.revokeObjectURL(a.href);
                      }} title="Export HTML" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <div className="w-px h-4 bg-[#2A2A2A] mx-0.5" />
                      <button onClick={() => setHistoryOpen(true)} title="Version History" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <History className="w-3.5 h-3.5" />
                      </button>
                    </>}

                    {/* ── Insert ── */}
                    {activeMenuTab === "insert" && <>
                      <button onClick={() => exec("insertUnorderedList")} title="Bullet List" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <List className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => exec("insertOrderedList")} title="Numbered List" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <ListOrdered className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={insertChecklist} title="Checklist" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <CheckSquare className="w-3.5 h-3.5" />
                      </button>
                      <div className="w-px h-4 bg-[#2A2A2A] mx-0.5" />
                      <button onClick={() => exec("formatBlock", "blockquote")} title="Blockquote" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <Quote className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => exec("formatBlock", "pre")} title="Code Block" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <Code className="w-3.5 h-3.5" />
                      </button>
                      <div className="w-px h-4 bg-[#2A2A2A] mx-0.5" />
                      <button onClick={() => exec("insertHorizontalRule")} title="Horizontal Divider" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => {
                        const tbl = document.createElement("table"); tbl.style.cssText = "border-collapse:collapse;width:100%;margin:8px 0";
                        for (let r = 0; r < 3; r++) { const tr = document.createElement("tr"); for (let c = 0; c < 3; c++) { const td = document.createElement("td"); td.contentEditable = "true"; td.style.cssText = "border:1px solid #2A2A2A;padding:6px 10px;min-width:80px;"; td.innerHTML = "&nbsp;"; tr.appendChild(td); } tbl.appendChild(tr); }
                        const sel = window.getSelection(); if (sel?.rangeCount > 0) { sel.getRangeAt(0).insertNode(tbl); } else { editorRef.current?.appendChild(tbl); }
                      }} title="Insert Table 3×3" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <TableIcon className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => {
                        const img = document.createElement("input"); img.type = "file"; img.accept = "image/*";
                        img.onchange = (e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = (ev) => exec("insertImage", ev.target.result); r.readAsDataURL(f); };
                        img.click();
                      }} title="Insert Image" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <ImageIcon className="w-3.5 h-3.5" />
                      </button>
                    </>}

                    {/* ── View ── */}
                    {activeMenuTab === "view" && <>
                      <button onClick={async () => {
                        if (!editorRef.current || !active?.doc_id) return;
                        setAiSummarizing(true);
                        try {
                          const text = editorRef.current.innerText?.slice(0, 8000) || "";
                          const { data } = await api.post("/ai/summarize", { text, doc_id: active.doc_id });
                          setAiSummary(data.summary || data.result || "");
                        } catch { toast.error("Summarize failed"); }
                        finally { setAiSummarizing(false); }
                      }} disabled={aiSummarizing} title="AI Summarize"
                        className="flex items-center gap-1 p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED] disabled:opacity-40">
                        <Sparkles className="w-3.5 h-3.5" />{aiSummarizing && <span className="text-[10px]">…</span>}
                      </button>
                      <div className="w-px h-4 bg-[#2A2A2A] mx-0.5" />
                      <button onClick={() => { const el = document.querySelector("[data-testid='doc-stats-toast']"); if (el) el.click(); }} title="Word Count" className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]">
                        <BarChart2 className="w-3.5 h-3.5" />
                      </button>
                      <div className="w-px h-4 bg-[#2A2A2A] mx-0.5" />
                      <button onClick={() => setReadMode(v => !v)} title="Read Mode"
                        className={`p-1.5 rounded hover:bg-[#1a1a1a] transition-colors ${readMode ? "text-[#5A90F5]" : "text-[#A1A1AA] hover:text-[#EDEDED]"}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setShowToc(v => !v)} title="Table of contents"
                        className={`p-1.5 rounded hover:bg-[#1a1a1a] transition-colors ${showToc ? "text-[#5A90F5]" : "text-[#A1A1AA] hover:text-[#EDEDED]"}`}>
                        <List className="w-3.5 h-3.5" />
                      </button>
                    </>}

                    {/* ── Text ── formatting icons, with persistent collapse toggle */}
                    {activeMenuTab === "text" && (
                      <>
                      {topBarCollapsed ? (
                        <button onClick={() => setTopBarCollapsed(false)}
                          className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[#1a1a1a] text-[#52525B] hover:text-[#A1A1AA] text-[10px]"
                          title="Show formatting toolbar">
                          <ChevronDown className="w-3 h-3 -rotate-90" /> Show formatting
                        </button>
                      ) : <>
                  {/* Order: Font → Size → Line spacing (user request iter26).
                      iter33: FontPicker replaces the simple ThemedSelect —
                      adds search, recently-used (10), and Key-Pair Fonts. */}
                  <div onMouseDown={() => {
                    // Save to both the specific ref and the master savedSelRef
                    saveSelection();
                    try {
                      const sel = window.getSelection();
                      if (sel && sel.rangeCount > 0 && !sel.isCollapsed && editorRef.current?.contains(sel.anchorNode)) {
                        fontFamilyRangeRef.current = sel.getRangeAt(0).cloneRange();
                      }
                    } catch {}
                  }}>
                  <FontPicker
                    value={fontFamily || "Inter"}
                    onChange={(v) => applyFontFamily(v)}
                    data-testid="docs-fontname"
                  />
                  </div>
                  {/* Iter39 fix: previous version called applyFontSize on
                      every keystroke (onChange), which extracted the
                      editor selection on each digit and clobbered it —
                      so users couldn't actually finish typing a
                      multi-digit size before their selection was lost.
                      Now: capture selection on focus, apply on Enter or
                      blur. */}
                  <input
                    type="number"
                    min="8" max="96"
                    value={fontSizeDraft}
                    onMouseDown={() => {
                      // Capture selection NOW — before this input steals focus
                      saveSelection();
                      try {
                        const sel = window.getSelection();
                        if (sel && sel.rangeCount > 0 && !sel.isCollapsed && editorRef.current?.contains(sel.anchorNode)) {
                          fontSizeRangeRef.current = sel.getRangeAt(0).cloneRange();
                        }
                      } catch {}
                    }}
                    onFocus={() => {
                      // Also try on focus as a fallback (keyboard navigation).
                      saveSelection();
                      try {
                        const sel = window.getSelection();
                        if (sel && sel.rangeCount > 0 && !sel.isCollapsed && editorRef.current?.contains(sel.anchorNode)) {
                          fontSizeRangeRef.current = sel.getRangeAt(0).cloneRange();
                        }
                      } catch {}
                      setFontSizeDraft(String(fontSizePx || 14));
                    }}
                    onChange={(e) => setFontSizeDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); commitFontSize(); }
                      if (e.key === "Escape") { e.preventDefault(); fontSizeRangeRef.current = null; setFontSizeDraft(String(fontSizePx || 14)); editorRef.current?.focus(); }
                    }}
                    onBlur={commitFontSize}
                    title="Font size — type a number, press Enter to apply"
                    className="cresc-no-spinner w-12 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                    data-testid="docs-fontsize"
                  />
                  <ThemedSelect
                    value={String(lineHeight)}
                    onMouseDown={saveSelection}
                    onChange={(v) => {
                      if (v === "__custom__") {
                        const next = window.prompt("Custom line height (e.g. 1.35):", String(lineHeight));
                        if (next && !Number.isNaN(parseFloat(next))) applyLineHeight(parseFloat(next));
                        return;
                      }
                      applyLineHeight(parseFloat(v));
                    }}
                    options={[
                      { value: "0",     label: "0" },
                      { value: "0.25",  label: "1/4" },
                      { value: "0.5",   label: "1/2" },
                      { value: "0.75",  label: "3/4" },
                      { value: "1",     label: "1" },
                      { value: "1.5",   label: "1 1/2" },
                      { value: "2",     label: "2" },
                      { value: "__custom__", label: "Custom…" },
                    ]}
                    className="w-20"
                    data-testid="docs-linespacing"
                  />
                  <div className="w-px h-4 bg-[#2A2A2A] mx-1" />
                  <button onClick={() => exec("bold")} className="p-1.5 rounded hover:bg-[#1a1a1a]" title="Bold"><Bold className="w-3.5 h-3.5" /></button>
                  <button onClick={() => exec("italic")} className="p-1.5 rounded hover:bg-[#1a1a1a]" title="Italic"><Italic className="w-3.5 h-3.5" /></button>
                  <button onClick={() => exec("underline")} className="p-1.5 rounded hover:bg-[#1a1a1a]" title="Underline"><UnderlineIcon className="w-3.5 h-3.5" /></button>
                  <button onClick={() => exec("strikeThrough")} className="p-1.5 rounded hover:bg-[#1a1a1a]" title="Strikethrough"><Strikethrough className="w-3.5 h-3.5" /></button>
                  <div className="w-px h-4 bg-[#2A2A2A] mx-1" />
                  <button onClick={() => { applyHeading("p"); setCurrentBlock("p"); }} className={`p-1.5 rounded text-[10px] uppercase tracking-wider ${currentBlock === "p" ? "bg-[#2A2A2A] text-[#EDEDED]" : "hover:bg-[#1a1a1a] text-[#A1A1AA]"}`} title="Body" data-testid="docs-body">Body</button>
                  <button onClick={() => { applyHeading("h1"); setCurrentBlock("h1"); }} className={`p-1.5 rounded ${currentBlock === "h1" ? "bg-[#2A2A2A]" : "hover:bg-[#1a1a1a]"}`} title="Heading 1" data-testid="docs-h1"><Heading1 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => { applyHeading("h2"); setCurrentBlock("h2"); }} className={`p-1.5 rounded ${currentBlock === "h2" ? "bg-[#2A2A2A]" : "hover:bg-[#1a1a1a]"}`} title="Heading 2" data-testid="docs-h2"><Heading2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => { applyHeading("h3"); setCurrentBlock("h3"); }} className={`p-1.5 rounded ${currentBlock === "h3" ? "bg-[#2A2A2A]" : "hover:bg-[#1a1a1a]"}`} title="Heading 3" data-testid="docs-h3"><Heading3 className="w-3.5 h-3.5" /></button>
                  <div className="w-px h-4 bg-[#2A2A2A] mx-1" />
                  {/* Justification — ThemedSelect with chevron (user
                      request iter26): single-button dropdown with align
                      icons. */}
                  <ThemedSelect
                    value="left"
                    onChange={(v) => exec("justify" + v.charAt(0).toUpperCase() + v.slice(1))}
                    options={[
                      { value: "left",   label: "Left",   render: () => <AlignLeft className="w-3.5 h-3.5" /> },
                      { value: "center", label: "Center", render: () => <AlignCenter className="w-3.5 h-3.5" /> },
                      { value: "right",  label: "Right",  render: () => <AlignRight className="w-3.5 h-3.5" /> },
                      { value: "Full",   label: "Justify", render: () => <span className="text-[10px]">≡</span> },
                    ]}
                    className="w-14"
                    data-testid="docs-justify"
                  />

                  <div className="flex-1" />
                  {activeCell && (
                    <div className="flex items-center gap-1" data-testid="docs-table-tools">
                      <button onClick={addTableRow} className="px-2 py-0.5 rounded border border-[#5A90F5]/40 text-[10px] text-[#5A90F5] hover:bg-[#5A90F5]/10" data-testid="docs-table-addrow">+ Row</button>
                      <button onClick={addTableCol} className="px-2 py-0.5 rounded border border-[#5A90F5]/40 text-[10px] text-[#5A90F5] hover:bg-[#5A90F5]/10" data-testid="docs-table-addcol">+ Col</button>
                    </div>
                  )}
                  <button onClick={() => exec("removeFormat")} className="px-2 py-0.5 rounded hover:bg-[#1a1a1a] text-[10px] uppercase tracking-wider" title="Clear formatting"><Type className="w-3 h-3 inline mr-1" /> Clear</button>
                  <TextColorControl exec={exec} userPickedTextColorRef={userPickedTextColorRef} data-testid="docs-textcolor" />
                  <HighlightRoster exec={exec} userPickedTextColorRef={userPickedTextColorRef} setLastForeColor={setLastForeColor} editorRef={editorRef} data-testid="docs-hl-roster" />
                  <button onClick={() => setTopBarCollapsed(true)} className="ml-auto p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] flex-shrink-0" title="Hide formatting" data-testid="docs-topbar-toggle">
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  </>}
                  </>
                  )}

                  </div>{/* end Row 2 */}
                </div>{/* end docs-toolbar */}
        {/* Row 2: open tabs */}
        <div className="border-b border-[#2A2A2A] flex items-center gap-1 px-3 py-1 overflow-x-auto bg-[#0D0D0D]" data-testid="docs-tab-bar">
          <div className="flex items-center gap-1 flex-1 min-w-0">
            {tabs.map((tid) => {
              const d = docs.find((x) => x.doc_id === tid);
              if (!d) return null;
              const isActive = activeId === tid;
              return (
                <div
                  key={tid}
                  className={`group flex items-center gap-1.5 px-2.5 py-1 text-[11px] cursor-pointer whitespace-nowrap rounded-md ${isActive ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#141414]"}`}
                  data-testid={`docs-tab-${tid}`}
                  data-docs-open-tab={tid}
                  onClick={() => setActiveId(tid)}
                >
                  <FileText className="w-3 h-3 flex-shrink-0 opacity-70" />
                  <span className="max-w-[140px] truncate">{d.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); closeTab(tid); }}
                    className="opacity-0 group-hover:opacity-60 hover:!opacity-100 p-0.5 rounded hover:bg-[#2a2a2a]"
                    data-testid={`docs-tab-close-${tid}`}
                    title="Close tab"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

                {/* ── Page Options panel ── */}
                {pageOptsOpen && (
                  <div className="border-b border-[#2A2A2A] bg-[#0D0D0D] px-4 py-3 flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">Page</span>
                      <button onClick={togglePageMode}
                        className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${pageMode ? "border-[#5A90F5] text-[#5A90F5] bg-[#5A90F5]/10" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#3A3A3A]"}`}>
                        {pageMode ? "Page view on" : "Page view off"}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#A1A1AA]">Color</span>
                      <label className="w-6 h-6 rounded border border-[#2A2A2A] cursor-pointer overflow-hidden relative" style={{ background: pageOpts.color }}>
                        <input type="color" value={pageOpts.color}
                          onChange={e => savePageOpts({ ...pageOpts, color: e.target.value, textColor: "auto" })}
                          className="opacity-0 absolute inset-0 w-full h-full cursor-pointer" />
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#A1A1AA]">Text</span>
                      <button onClick={() => savePageOpts({ ...pageOpts, textColor: "auto" })}
                        className={`text-[9px] px-1.5 py-0.5 rounded border ${pageOpts.textColor === "auto" ? "border-[#5A90F5] text-[#5A90F5]" : "border-[#2A2A2A] text-[#52525B] hover:border-[#3A3A3A]"}`}>Auto</button>
                      <label className="w-6 h-6 rounded border border-[#2A2A2A] cursor-pointer overflow-hidden relative"
                        style={{ background: pageOpts.textColor !== "auto" ? pageOpts.textColor : pageTextColor }}>
                        <input type="color" value={pageOpts.textColor !== "auto" ? pageOpts.textColor : pageTextColor}
                          onChange={e => savePageOpts({ ...pageOpts, textColor: e.target.value })}
                          className="opacity-0 absolute inset-0 w-full h-full cursor-pointer" />
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#A1A1AA]">Size</span>
                      <select value={pageOpts.size} onChange={e => savePageOpts({ ...pageOpts, size: e.target.value })}
                        className="bg-[#141414] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[10px] text-[#EDEDED] outline-none cursor-pointer">
                        <option value="letter">Letter (8.5×11)</option>
                        <option value="a4">A4 (210×297mm)</option>
                        <option value="legal">Legal (8.5×14)</option>
                        <option value="a5">A5 (148×210mm)</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#A1A1AA]">Margins</span>
                      <select value={pageOpts.margins} onChange={e => savePageOpts({ ...pageOpts, margins: e.target.value })}
                        className="bg-[#141414] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[10px] text-[#EDEDED] outline-none cursor-pointer">
                        <option value="none">None</option>
                        <option value="narrow">Narrow</option>
                        <option value="normal">Normal (1 in)</option>
                        <option value="wide">Wide (1.5 in)</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => savePageOpts({ ...pageOpts, seamless: !pageOpts.seamless })}
                        className={`text-[9px] px-2 py-0.5 rounded border transition-colors ${pageOpts.seamless ? "border-[#5A90F5] text-[#5A90F5]" : "border-[#2A2A2A] text-[#52525B] hover:border-[#3A3A3A]"}`}>
                        {pageOpts.seamless ? "Seamless" : "Split pages"}
                      </button>
                      <button onClick={() => savePageOpts({ ...pageOpts, glass: !pageOpts.glass })}
                        className={`text-[9px] px-2 py-0.5 rounded border transition-colors ${pageOpts.glass ? "border-[#5A90F5] text-[#5A90F5]" : "border-[#2A2A2A] text-[#52525B] hover:border-[#3A3A3A]"}`}>
                        Glass effect
                      </button>
                    </div>
                  </div>
                )}

                {aiSummary && (
                  <div className="w-full flex justify-center px-10 mt-3 mb-4">
                    <div className="w-full max-w-4xl bg-[#141414] border border-[#2A2A2A] rounded-lg px-4 py-4 pb-5 text-[12px] text-[#A1A1AA] relative">
                      <span className="text-[#5A90F5] font-medium text-[10px] uppercase tracking-wider mr-2">✦ AI Summary</span>
                      {aiSummary}
                      <button onClick={() => setAiSummary(null)} className="absolute top-2 right-2 text-[#3F3F46] hover:text-[#A1A1AA] text-base leading-none">×</button>
                    </div>
                  </div>
                )}
                {active?.kind === "master" && <></>}
                {/* Page container: scrollable dark bg in page mode, normal flex otherwise */}
                <div className={pageMode ? "flex-1 overflow-y-auto" : "flex-1 flex flex-col"}
                  style={pageMode ? { background: "transparent" } : undefined}
                  onClick={pageMode ? (e) => { if (e.target === e.currentTarget) editorRef.current?.focus(); } : undefined}>
                {/* Paged layout: renders multiple page-bg cards behind one editor */}
                {pageMode ? (
                <MultiPageEditor
                  ref={multiPageRef}
                  pageOpts={pageOpts}
                  PAGE_SIZES={PAGE_SIZES} PAGE_MARGINS={PAGE_MARGINS}
                  pageTextColor={pageTextColor}
                  editorRef={editorRef}
                  suppressContentEditableWarning
                  onKeyDown={onEditorKeyDown}
                  onBlur={(e) => { saveSelection(e); setTimeout(() => setShorthandPreview(null), 200); }}
                  onMouseUp={saveSelection}
                  onKeyUp={(e) => { if (e.shiftKey || e.key === "End" || e.key === "Home") saveSelection(); }}
                  onInput={(e) => {
                    setDirty(true);
                    updateCurrentBlock();
                    try { onEditorInput_slash(); } catch {}
                    // Buffer unsaved content in localStorage so it survives navigation
                    if (active) {
                      try {
                        const bufKey = `cresc-doc-buffer:${active.doc_id}${activeSubtabId ? `:${activeSubtabId}` : ""}`;
                        localStorage.setItem(bufKey, editorRef.current?.innerHTML || "");
                      } catch {}
                    }
                    // @mention detection
                    const sel2 = window.getSelection();
                    if (sel2?.rangeCount > 0 && sel2.isCollapsed) {
                      const r2 = sel2.getRangeAt(0);
                      const node2 = r2.startContainer;
                      if (node2.nodeType === 3) {
                        const text2 = node2.textContent.slice(0, r2.startOffset);
                        const atIdx = text2.lastIndexOf("@");
                        if (atIdx !== -1) {
                          const query = text2.slice(atIdx + 1);
                          if (!/\s/.test(query) && query.length <= 20) {
                            // Show mention picker
                            const rng = r2.cloneRange();
                            rng.collapse(false);
                            const rect = rng.getBoundingClientRect();
                            if (!wsMembers.length && currentWs?.workspace_id) {
                              api.get(`/workspaces/${currentWs.workspace_id}/members`).then(({ data }) => setWsMembers(data || [])).catch(() => {});
                            }
                            setMentionPicker({ x: rect.left, y: rect.bottom + 4, query });
                          } else {
                            setMentionPicker(null);
                          }
                        } else {
                          setMentionPicker(null);
                        }
                      }
                    }
                    if (shorthands?.length > 0) {
                      const sel = window.getSelection();
                      if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return;
                      const range = sel.getRangeAt(0);
                      const node = range.startContainer;
                      if (node.nodeType !== 3) return;
                      const text = node.textContent.slice(0, range.startOffset);
                      // Check each shorthand trigger
                      for (const sh of shorthands) {
                        if (!sh.trigger) continue;
                        if (text.endsWith(sh.trigger)) {
                          // Replace the trigger text with a chip element
                          const r2 = range.cloneRange();
                          r2.setStart(node, range.startOffset - sh.trigger.length);
                          r2.setEnd(node, range.startOffset);
                          r2.deleteContents();
                          const chip = document.createElement("span");
                          chip.className = "cresc-shorthand-chip";
                          chip.contentEditable = "false";
                          chip.dataset.entryId = sh.entry_id;
                          chip.dataset.trigger = sh.trigger;
                          chip.textContent = sh.expansion;
                          chip.title = `Shorthand: ${sh.trigger} → ${sh.expansion}`;
                          r2.insertNode(chip);
                          // Move cursor after chip
                          const after = range.cloneRange();
                          after.setStartAfter(chip);
                          after.collapse(true);
                          sel.removeAllRanges();
                          sel.addRange(after);
                          setDirty(true);
                          break;
                        }
                      }
                      // Predictor: find partial match for current word being typed
                      const sel2 = window.getSelection();
                      if (sel2 && sel2.rangeCount > 0 && sel2.isCollapsed) {
                        const r3 = sel2.getRangeAt(0);
                        const n3 = r3.startContainer;
                        if (n3.nodeType === 3) {
                          const txt = n3.textContent.slice(0, r3.startOffset);
                          const words = txt.split(/\s+/);
                          const currentWord = words[words.length - 1];
                          if (currentWord.length > 0) {
                            const exactMatch = shorthands.find(s => s.trigger === currentWord);
                            const partialMatch = !exactMatch && shorthands.find(s => s.trigger?.startsWith(currentWord) && s.trigger !== currentWord);
                            if (exactMatch) setShorthandPreview({ trigger: exactMatch.trigger, expansion: exactMatch.expansion, partial: false });
                            else if (partialMatch) setShorthandPreview({ trigger: partialMatch.trigger, expansion: partialMatch.expansion, partial: true });
                            else setShorthandPreview(null);
                          } else {
                            setShorthandPreview(null);
                          }
                        }
                      }
                    }
                  }}
                  onClick={(e) => {
                    // Toggle cresc-checkitem--done when a checkbox is clicked
                    if (e.target.type === "checkbox") {
                      const item = e.target.closest(".cresc-checkitem");
                      if (item) {
                        item.classList.toggle("cresc-checkitem--done", e.target.checked);
                        setDirty(true);
                      }
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const html = e.clipboardData?.getData("text/html");
                    const plain = e.clipboardData?.getData("text/plain");

                    // If no html or content is very large, just insert plain text
                    // Plain text avoids all the layout/page explosion issues
                    if (!html || (plain && plain.length > 5000)) {
                      if (plain) {
                        // Convert plain text to minimal HTML — paragraphs only
                        const paragraphs = plain
                          .split(/\n{2,}/)
                          .map(p => `<p>${p.replace(/\n/g, "<br>").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`)
                          .join("");
                        exec("insertHTML", paragraphs || plain);
                      }
                      setDirty(true);
                      return;
                    }

                    const tmp = document.createElement("div");
                    tmp.innerHTML = html;

                    // Strip inline styles that cause formatting chaos
                    tmp.querySelectorAll("[style]").forEach(el => {
                      el.style.lineHeight = "";
                      el.style.color = "";
                      el.style.fontSize = "";
                      el.style.fontFamily = "";
                      el.style.backgroundColor = "";
                      if (!el.getAttribute("style")?.trim()) el.removeAttribute("style");
                    });

                    // Remove MS Word / Google Docs meta tags
                    tmp.querySelectorAll("meta,style,script,link").forEach(el => el.remove());

                    // Collapse excessive <br> sequences (3+ → 1 paragraph break)
                    tmp.innerHTML = tmp.innerHTML.replace(/(<br\s*\/?>\s*){3,}/gi, "</p><p>");

                    // Handle list/heading context
                    const sel = window.getSelection();
                    const curLi = sel?.anchorNode && (
                      sel.anchorNode.nodeType === 3
                        ? sel.anchorNode.parentElement?.closest("li")
                        : sel.anchorNode.closest?.("li")
                    );
                    if (curLi) {
                      tmp.querySelectorAll("ul,ol").forEach(list => {
                        while (list.firstChild) list.parentNode.insertBefore(list.firstChild, list);
                        list.remove();
                      });
                    }

                    exec("insertHTML", tmp.innerHTML);
                    setDirty(true);
                  }}
                  onKeyDown={onEditorKeyDown}
                  onContextMenu={onEditorContextMenu}
                  onClick={(e) => {
                    // Checkbox click — toggle strikethrough + dim on the checkitem and its children
                    if (e.target.type === "checkbox") {
                      const checkitem = e.target.closest(".cresc-checkitem");
                      if (checkitem) {
                        const checked = e.target.checked;
                        // Sync the HTML attribute so innerHTML captures the state on save
                        if (checked) e.target.setAttribute("checked", "");
                        else e.target.removeAttribute("checked");
                        checkitem.classList.toggle("cresc-checkitem--done", checked);
                        checkitem.querySelectorAll(".cresc-checkitem, li").forEach(child => {
                          child.classList.toggle("cresc-checkitem--child-done", checked);
                        });
                        setDirty(true);
                        // Auto-save checkbox state immediately — users expect
                        // checkboxes to persist without a manual save.
                        setTimeout(() => saveRef.current?.(), 300);
                      }
                      return;
                    }
                    // Bullet click — clicking inside an <li> on the
                    // bullet glyph (i.e. left of the text content) opens
                    // the bullet shape picker. Double-click selects ALL
                    // bullets in that list and the picker affects them
                    // all. Iter32 user request.
                    const li = e.target.closest && e.target.closest("li");
                    if (li) {
                      const liRect = li.getBoundingClientRect();
                      // Bullet glyph sits in the ::marker pseudo-element which
                      // is in the left gutter. Hit zone is left 32px to catch
                      // both compact and indented list styles.
                      if (e.clientX - liRect.left < 32) {
                        e.preventDefault();
                        const ol = li.parentElement;
                        const detail = e.detail >= 2 ? "all" : "single";
                        window.dispatchEvent(new CustomEvent("crescendo:bullet-shape-pick", {
                          detail: { x: e.clientX, y: e.clientY, list: ol, target: detail === "all" ? "all" : li },
                        }));
                        return;
                      }
                    }
                    // Click-to-add row/col on the bottom or right edges of a
                    // table cell.
                    const cell = e.target.closest && e.target.closest("td, th");
                    if (!cell) return;
                    const rect = cell.getBoundingClientRect();
                    const padX = rect.width - (e.clientX - rect.left);
                    const padY = rect.height - (e.clientY - rect.top);
                    const HIT = 7;
                    const onRight = padX <= HIT && padX >= 0;
                    const onBottom = padY <= HIT && padY >= 0;
                    if (!onRight && !onBottom) return;
                    e.preventDefault();
                    const tr = cell.parentElement;
                    const table = cell.closest("table");
                    if (!table || !tr) return;
                    if (onBottom) {
                      const newTr = document.createElement("tr");
                      for (let i = 0; i < tr.children.length; i++) {
                        const td = document.createElement("td");
                        td.contentEditable = "true";
                        td.style.cssText = "border:1px solid #2A2A2A;padding:6px 10px;min-width:80px;";
                        td.innerHTML = "&nbsp;";
                        newTr.appendChild(td);
                      }
                      tr.parentElement.insertBefore(newTr, tr.nextSibling);
                    }
                    if (onRight) {
                      const colIdx = Array.from(tr.children).indexOf(cell);
                      table.querySelectorAll("tr").forEach((row) => {
                        const td = document.createElement("td");
                        td.contentEditable = "true";
                        td.style.cssText = "border:1px solid #2A2A2A;padding:6px 10px;min-width:80px;";
                        td.innerHTML = "&nbsp;";
                        const sib = row.children[colIdx];
                        if (sib && sib.nextSibling) row.insertBefore(td, sib.nextSibling);
                        else row.appendChild(td);
                      });
                    }
                    setDirty(true);
                  }}
                  className={`cresc-doc-editor outline-none text-sm leading-relaxed ${pageMode ? "" : "flex-1 overflow-y-auto prose-invert max-w-4xl w-full self-center"} ${readMode && !pageMode ? "px-0 py-0" : !pageMode ? "px-10 py-10" : ""}`}
                  data-page-mode={pageMode ? "true" : undefined}
                  style={pageMode ? (() => {
                    const sz = PAGE_SIZES[pageOpts.size] || PAGE_SIZES.letter;
                    const mg = PAGE_MARGINS[pageOpts.margins] ?? 96;
                    return {
                      width: sz.w,
                      minHeight: sz.h,
                      padding: mg,
                      background: pageOpts.glass
                        ? `rgba(${parseInt((pageOpts.color||"#1e1e1e").slice(1,3),16)},${parseInt((pageOpts.color||"#1e1e1e").slice(3,5),16)},${parseInt((pageOpts.color||"#1e1e1e").slice(5,7),16)},0.85)`
                        : (pageOpts.color || "#1e1e1e"),
                      backdropFilter: pageOpts.glass ? "blur(12px) saturate(1.4)" : "none",
                      WebkitBackdropFilter: pageOpts.glass ? "blur(12px) saturate(1.4)" : "none",
                      color: pageTextColor,
                      boxShadow: "0 4px 32px rgba(0,0,0,0.5)",
                      borderRadius: 3,
                      alignSelf: "center",
                      flexShrink: 0,
                    };
                  })() : { minHeight: 500 }}
                  data-testid="docs-rich-editor" data-readmode={readMode}
                />
                ) : (
                <div className={`cresc-doc-editor flex-1 overflow-y-auto outline-none text-sm leading-relaxed prose-invert max-w-4xl w-full self-center ${readMode ? "px-0 py-0" : "px-10 py-10"}`}
                  style={{ minHeight: 500 }}
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  data-testid="docs-rich-editor" data-readmode={readMode}
                />
                )}
                {inkMode && (
                  <InkOverlay
                    editorRef={editorRef}
                    onInsertText={text => {
                      if (editorRef.current) {
                        editorRef.current.focus();
                        document.execCommand("insertText", false, text);
                        setDirty(true);
                      }
                      setInkMode(false);
                    }}
                    onClose={() => setInkMode(false)}
                  />
                )}
                </div>{/* end page-container */}
              </>
            )}
          </>
        )}
        {/* Iter39: doc stats toast — small horizontal pill INSIDE the
            doc environment, lower-left. Anchors against the relative
            parent column. */}
        {active && active.kind !== "link" && (
          <DocumentStatsToast editorRef={editorRef} body={body} name={name} />
        )}
      </div>

      {/* Comments side rail — only when toggled and a doc is active. */}
      {commentsOpen && active && (
        <DocumentComments
          docId={active.doc_id}
          onClose={() => setCommentsOpen(false)}
        />
      )}
      {/* Shorthand predictor — fixed bottom-right of document, shows match as user types */}
      {shorthandPreview && (
        <div
          className="fixed z-[9980] flex items-center gap-2 px-3 py-2 rounded-xl shadow-2xl border border-[#2A2A2A] bg-[#111] cursor-pointer select-none"
          style={{ bottom: 24, right: 24, maxWidth: 260 }}
          onMouseDown={e => {
            e.preventDefault();
            // Apply the expansion at the cursor
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0 && sel.isCollapsed) {
              const range = sel.getRangeAt(0);
              const node = range.startContainer;
              if (node.nodeType === 3) {
                const txt = node.textContent.slice(0, range.startOffset);
                const words = txt.split(/\s+/);
                const currentWord = words[words.length - 1];
                const trigLen = currentWord.length;
                if (trigLen > 0) {
                  const r2 = range.cloneRange();
                  r2.setStart(node, range.startOffset - trigLen);
                  r2.setEnd(node, range.startOffset);
                  r2.deleteContents();
                  const chip = document.createElement("span");
                  chip.className = "cresc-shorthand-chip";
                  chip.contentEditable = "false";
                  chip.dataset.trigger = shorthandPreview.trigger;
                  chip.textContent = shorthandPreview.expansion;
                  chip.title = `Shorthand: ${shorthandPreview.trigger} → ${shorthandPreview.expansion}`;
                  r2.insertNode(chip);
                  const after = range.cloneRange();
                  after.setStartAfter(chip);
                  after.collapse(true);
                  sel.removeAllRanges();
                  sel.addRange(after);
                  setDirty(true);
                }
              }
            }
            setShorthandPreview(null);
          }}
        >
          <span className="text-[10px] text-[#52525B] shrink-0">{shorthandPreview.partial ? "→" : "✓"}</span>
          <span className="text-[11px] text-[#A1A1AA] shrink-0 font-mono">{shorthandPreview.trigger}</span>
          <span className="text-[10px] text-[#52525B]">→</span>
          <span className="text-[11px] text-[#EDEDED] truncate">{shorthandPreview.expansion}</span>
          <button
            className="ml-1 text-[#52525B] hover:text-[#EDEDED] text-[10px] shrink-0"
            onMouseDown={e => { e.stopPropagation(); e.preventDefault(); setShorthandPreview(null); }}
          >✕</button>
        </div>
      )}
      {/* Table of Contents — floating panel listing all headings in active doc */}
      {showToc && editorRef.current && (() => {
        const headings = [...editorRef.current.querySelectorAll("h1,h2,h3,h4")].map(el => ({
          level: parseInt(el.tagName[1]),
          text: el.innerText?.trim(),
          el,
        })).filter(h => h.text);
        if (!headings.length) return null;
        return (
          <div className="fixed right-4 top-1/2 -translate-y-1/2 z-[9970] w-48 bg-[#111] border border-[#2A2A2A] rounded-xl shadow-2xl p-3 max-h-[60vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] text-[#52525B] uppercase tracking-wider">Contents</span>
              <button onClick={() => setShowToc(false)} className="text-[#52525B] hover:text-[#EDEDED]">✕</button>
            </div>
            {headings.map((h, i) => (
              <button key={i} onClick={() => h.el.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="w-full text-left py-0.5 text-[11px] text-[#A1A1AA] hover:text-[#EDEDED] truncate transition-colors"
                style={{ paddingLeft: `${(h.level - 1) * 10}px` }}>
                {h.text}
              </button>
            ))}
          </div>
        );
      })()}
      {/* @mention picker */}
      {mentionPicker && createPortal(
        <div className="fixed z-[9990] bg-[#141414] border border-[#2A2A2A] rounded-lg shadow-2xl py-1 w-52"
          style={{ left: mentionPicker.x, top: mentionPicker.y }}
          onMouseDown={e => e.preventDefault()}
        >
          {wsMembers.filter(m => !mentionPicker.query || m.name?.toLowerCase().includes(mentionPicker.query.toLowerCase())).slice(0, 6).map(member => (
            <button key={member.user_id}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#1a1a1a] text-left"
              onClick={() => {
                // Replace @query with @name chip in editor
                const sel = window.getSelection();
                if (sel?.rangeCount > 0) {
                  const r = sel.getRangeAt(0);
                  const node = r.startContainer;
                  if (node.nodeType === 3) {
                    const text = node.textContent.slice(0, r.startOffset);
                    const atIdx = text.lastIndexOf("@");
                    const r2 = r.cloneRange();
                    r2.setStart(node, atIdx);
                    r2.deleteContents();
                    const chip = document.createElement("span");
                    chip.className = "cresc-mention-chip";
                    chip.contentEditable = "false";
                    chip.dataset.userId = member.user_id;
                    chip.textContent = `@${member.name || member.email}`;
                    r2.insertNode(chip);
                    const after = r2.cloneRange();
                    after.setStartAfter(chip);
                    after.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(after);
                  }
                }
                setMentionPicker(null);
                setDirty(true);
              }}
            >
              {member.picture && <img src={member.picture} alt="" className="w-5 h-5 rounded-full flex-shrink-0" />}
              <span className="text-[12px] text-[#EDEDED] truncate">{member.name || member.email}</span>
            </button>
          ))}
          {wsMembers.filter(m => !mentionPicker.query || m.name?.toLowerCase().includes(mentionPicker.query.toLowerCase())).length === 0 && (
            <p className="px-3 py-2 text-[11px] text-[#52525B]">No members found</p>
          )}
        </div>,
        document.body
      )}
      {/* Page link picker — opened from / Link to page slash command */}
      {pageLinkMenu && createPortal(
        <>
          <div className="fixed inset-0 z-[9989]" onClick={() => setPageLinkMenu(null)} />
          <div className="fixed z-[9990] bg-[#141414] border border-[#2A2A2A] rounded-lg shadow-2xl py-1 w-64"
            style={{ left: pageLinkMenu.x, top: pageLinkMenu.y }}
            onMouseDown={e => e.preventDefault()}
          >
            <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-[#52525B] border-b border-[#1a1a1a]">Link to page</div>
            {docs.length === 0 && <p className="px-3 py-2 text-[11px] text-[#52525B]">No documents yet</p>}
            {docs.slice(0, 12).map(doc => (
              <button key={doc.doc_id}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#1a1a1a] text-left"
                onClick={() => {
                  const chip = document.createElement("a");
                  chip.className = "cresc-page-link-chip";
                  chip.contentEditable = "false";
                  chip.dataset.docId = doc.doc_id;
                  chip.textContent = `📄 ${doc.name}`;
                  chip.title = `Link to: ${doc.name}`;
                  chip.href = "#";
                  chip.onclick = (ev) => {
                    ev.preventDefault();
                    window.dispatchEvent(new CustomEvent("crescendo:open-doc", { detail: { doc_id: doc.doc_id } }));
                  };
                  exec("insertHTML", chip.outerHTML);
                  setPageLinkMenu(null);
                  setDirty(true);
                }}
              >
                <span className="text-[11px] flex-shrink-0">📄</span>
                <span className="text-[12px] text-[#EDEDED] truncate flex-1">{doc.name}</span>
                {doc.kind === "master" && <span className="text-[9px] text-[#52525B]">master</span>}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
      {/* Iter37: Version History + Trash modals — mount at root of view. */}
      {(historyOpen || trashOpen) && (
        <UnifiedHistoryPanel
          active={active}
          workspaceId={active?.workspace_id || currentWs?.workspace_id}
          initialTab={trashOpen ? "trash" : "history"}
          onClose={() => { setHistoryOpen(false); setTrashOpen(false); }}
          onRestored={() => {
            try { window.dispatchEvent(new CustomEvent("crescendo:docs-changed")); } catch {}
            reload?.();
          }}
        />
      )}

      {/* Right-click context menu (Microsoft-Word-style). User request:
          - Always fully on-screen (clamped to viewport).
          - Adds Heading 3.
          - "Convert to" section (heading/body/quote/code/list with sub).
          - Horizontal toolbar above with Size / Font / Line height / Justify
            (justify = vertical dropdown), Text color + Highlight.
          - Numeric inputs show numbers (no labels), spinners hidden, Font is
            a themed dropdown. */}
      {ctx && createPortal((() => {
        // Clamp the menu so it fully fits inside the viewport.
        const W = 320, TOOL_H = 50, MENU_H = 360;
        const left = Math.min(ctx.x, window.innerWidth - W - 8);
        const top = Math.min(ctx.y, window.innerHeight - MENU_H - TOOL_H - 8);
        return (
          <div
            style={{ left, top, position: "fixed", zIndex: 9998, width: W }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
            data-testid="docs-ctx-menu"
            data-cresc-doc-ctx-menu="1"
          >
            {/* Horizontal formatting toolbar (above the right-click menu). */}
            <div className="bg-[#141414] border border-[#2A2A2A] rounded-md shadow-xl px-2 py-2 flex items-center gap-1.5 mb-1">
              <input
                type="number" step="0.05" min="0.5" max="3" value={lineHeight}
                onChange={(e) => applyLineHeight(e.target.value)}
                title="Line height"
                className="cresc-no-spinner w-12 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                data-testid="docs-ctx-linespacing"
              />
              <input
                type="number" min="8" max="96" value={fontSizePx || 14}
                onChange={(e) => applyFontSize(e.target.value)}
                title="Font size"
                className="cresc-no-spinner w-12 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                data-testid="docs-ctx-fontsize"
              />
              {(() => {
                const FONT_OPTS = [
                  { value: "Inter", label: "Inter" },
                  { value: "Helvetica", label: "Helvetica" },
                  { value: "Arial", label: "Arial" },
                  { value: "Georgia", label: "Georgia" },
                  { value: "Times New Roman", label: "Times New Roman" },
                  { value: "Menlo", label: "Menlo" },
                  { value: "Courier New", label: "Courier New" },
                  { value: "JetBrains Mono", label: "JetBrains Mono" },
                ];
                return (
                  <ThemedSelect
                    value={fontFamily || "Inter"}
                    onChange={(v) => applyFontFamily(v)}
                    options={FONT_OPTS.map((o) => ({
                      ...o,
                      render: () => <span style={{ fontFamily: o.value }}>{o.label}</span>,
                      style: { fontFamily: o.value },
                    }))}
                    className="flex-1 min-w-0"
                    data-testid="docs-ctx-fontname"
                  />
                );
              })()}
              <ThemedSelect
                value="Left"
                onChange={(v) => exec("justify" + v)}
                options={[
                  { value: "Left", label: "Left", render: () => <AlignLeft className="w-3.5 h-3.5" /> },
                  { value: "Center", label: "Center", render: () => <AlignCenter className="w-3.5 h-3.5" /> },
                  { value: "Right", label: "Right", render: () => <AlignRight className="w-3.5 h-3.5" /> },
                  { value: "Full", label: "Justify", render: () => <span className="text-[10px]">≡</span> },
                ]}
                className="w-12"
                data-testid="docs-ctx-justify"
              />
              {/* Text color + Highlight (themed dropdowns with presets,
                  recent colors and custom color, contrast-aware text). */}
              <TextColorControl exec={exec} userPickedTextColorRef={userPickedTextColorRef} data-testid="docs-ctx-textcolor" />
              <HighlightRoster exec={exec} userPickedTextColorRef={userPickedTextColorRef} setLastForeColor={setLastForeColor} editorRef={editorRef} data-testid="docs-ctx-hl-roster" />
            </div>
            {/* The actual right-click menu. */}
            <div className="bg-[#141414] border border-[#2A2A2A] rounded-md shadow-xl py-1 text-xs text-[#EDEDED] max-h-[420px] overflow-y-auto">
              <button onClick={() => { restoreCtxSelection(); document.execCommand("cut"); setDirty(true); setCtx(null); }} disabled={!ctx.hasSelection}
                className="w-full text-left px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-2 disabled:opacity-40" data-testid="docs-ctx-cut">
                <Scissors className="w-3.5 h-3.5" /> Cut
              </button>
              <button onClick={() => { restoreCtxSelection(); document.execCommand("copy"); setCtx(null); }} disabled={!ctx.hasSelection}
                className="w-full text-left px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-2 disabled:opacity-40" data-testid="docs-ctx-copy">
                <CopyIcon className="w-3.5 h-3.5" /> Copy
              </button>
              <button onClick={async () => {
                restoreCtxSelection();
                try {
                  const txt = await navigator.clipboard.readText();
                  if (txt) { document.execCommand("insertText", false, txt); setDirty(true); }
                } catch { document.execCommand("paste"); setDirty(true); }
                setCtx(null);
              }}
                className="w-full text-left px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-2" data-testid="docs-ctx-paste">
                <Clipboard className="w-3.5 h-3.5" /> Paste
              </button>
              <div className="my-1 border-t border-[#2A2A2A]" />
              <button onClick={() => { restoreCtxSelection(); exec("bold"); setCtx(null); }} disabled={!ctx.hasSelection}
                className="w-full text-left px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-2 disabled:opacity-40">
                <Bold className="w-3.5 h-3.5" /> Bold
              </button>
              <button onClick={() => { restoreCtxSelection(); exec("italic"); setCtx(null); }} disabled={!ctx.hasSelection}
                className="w-full text-left px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-2 disabled:opacity-40">
                <Italic className="w-3.5 h-3.5" /> Italic
              </button>
              <button onClick={() => { restoreCtxSelection(); exec("underline"); setCtx(null); }} disabled={!ctx.hasSelection}
                className="w-full text-left px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-2 disabled:opacity-40">
                <UnderlineIcon className="w-3.5 h-3.5" /> Underline
              </button>
              <div className="my-1 border-t border-[#2A2A2A]" />
              {/* Iter35 user request: "There should be an 'add comment'
                  option under the right-click menu for text." Wires to the
                  existing /api/documents/{id}/comments endpoint via the
                  Comments side panel. Restores selection so the comment
                  body gets the highlighted text as its `range_label`. */}
              <button
                onClick={() => {
                  restoreCtxSelection();
                  let snippet = "";
                  try {
                    const sel = window.getSelection();
                    if (sel && !sel.isCollapsed) snippet = (sel.toString() || "").trim().slice(0, 120);
                  } catch {}
                  setCommentsOpen(true);
                  // Hand the snippet off to the panel so it pre-fills.
                  window.dispatchEvent(new CustomEvent("crescendo:doc-add-comment", { detail: { docId: active?.doc_id, snippet } }));
                  setCtx(null);
                }}
                disabled={!ctx.hasSelection}
                className="w-full text-left px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-2 disabled:opacity-40"
                data-testid="docs-ctx-add-comment"
              >
                <MessageSquare className="w-3.5 h-3.5" /> Add Comment
              </button>
              <div className="my-1 border-t border-[#2A2A2A]" />
              {/* Iter35 user request: "We also need a 'set standard' button
                  under the right-click menu, where users can set selected
                  text as the standard for text types (H1, H2, H3; such as
                  the 'set headers to match' feature in Google Docs)." */}
              <button
                onClick={() => { restoreCtxSelection(); applySetAsStandard(); setCtx(null); }}
                disabled={!ctx.hasSelection}
                className="w-full text-left px-3 py-1.5 hover:bg-[#1a1a1a] flex items-center gap-2 disabled:opacity-40"
                data-testid="docs-ctx-set-standard"
                title="Apply selection's font + size + weight to all H1/H2/H3/body of the same level"
              >
                <Type className="w-3.5 h-3.5" /> Set as Standard
              </button>
              <div className="my-1 border-t border-[#2A2A2A]" />
              <div className="px-3 py-1 text-[#A1A1AA] text-[10px] uppercase tracking-wider">Convert To</div>
              {/* User request: condense convert-to into a themed dropdown
                  with H1 as the preview/default, including all block
                  types in one place. Restores the saved selection before
                  applying so the conversion lands on the user's text
                  even after clicking through the popover. */}
              <div className="px-3 py-1.5">
                <ThemedSelect
                  value="h1"
                  onChange={(v) => {
                    restoreCtxSelection();
                    if (v === "h1" || v === "h2" || v === "h3" || v === "p") applyHeading(v);
                    else if (v === "quote") applyQuote();
                    else if (v === "code") applyCode();
                    else if (v === "bullets") { document.execCommand("styleWithCSS", false, true); exec("insertUnorderedList"); }
                    else if (v === "numbers") { document.execCommand("styleWithCSS", false, true); exec("insertOrderedList"); }
                    else if (v === "checklist") insertChecklist();
                    setCtx(null);
                  }}
                  options={[
                    { value: "h1", label: "Heading 1", render: () => <span className="flex items-center gap-2"><Heading1 className="w-3.5 h-3.5" /> Heading 1</span> },
                    { value: "h2", label: "Heading 2", render: () => <span className="flex items-center gap-2"><Heading2 className="w-3.5 h-3.5" /> Heading 2</span> },
                    { value: "h3", label: "Heading 3", render: () => <span className="flex items-center gap-2"><Heading3 className="w-3.5 h-3.5" /> Heading 3</span> },
                    { value: "p",  label: "Body",       render: () => <span className="flex items-center gap-2"><Type className="w-3.5 h-3.5" /> Body</span> },
                    { value: "quote", label: "Quote",   render: () => <span className="flex items-center gap-2"><Quote className="w-3.5 h-3.5" /> Quote</span> },
                    { value: "code",  label: "Code",    render: () => <span className="flex items-center gap-2"><Code className="w-3.5 h-3.5" /> Code Block</span> },
                    { value: "bullets",   label: "Bulleted",   render: () => <span className="flex items-center gap-2"><List className="w-3.5 h-3.5" /> Bulleted List</span> },
                    { value: "numbers",   label: "Numbered",   render: () => <span className="flex items-center gap-2"><ListOrdered className="w-3.5 h-3.5" /> Numbered List</span> },
                    { value: "checklist", label: "Checkbox",   render: () => <span className="flex items-center gap-2"><CheckSquare className="w-3.5 h-3.5" /> Checkbox List</span> },
                  ]}
                  className="w-full"
                  data-testid="docs-ctx-convert"
                />
              </div>
            </div>
          </div>
        );
      })(), document.body)}

      {/* Per-doc Share dialog — full modes parity with workspaces. */}
      {slashMenu && (
        <div className="cresc-slash-popup" style={{ left: slashMenu.x, top: slashMenu.y }}>
          {slashMenu.items.map(item => (
            <div key={item.id} className="cresc-slash-item" onMouseDown={e => { e.preventDefault(); applySlashCommand(item, slashMenu?.node, slashMenu?.slashOffset, slashMenu?.query); }}>
              <span className="cresc-slash-icon">{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* AI Writing Assistant popup */}
      {aiAssist && (
        <>
          <div className="fixed inset-0 z-[160]" onClick={() => { setAiAssist(null); setAiAssistResult(null); }} />
          <div
            className="fixed z-[161] bg-[#141414] border border-[#2A2A2A] rounded-lg shadow-2xl w-80 overflow-hidden"
            style={{ left: Math.min(aiAssist.x, window.innerWidth - 340), top: Math.min(aiAssist.y, window.innerHeight - 320) }}
            onClick={e => e.stopPropagation()}
          >
            <div className="px-3 py-2 border-b border-[#2A2A2A] flex items-center gap-2">
              <span className="text-[#5A90F5] text-sm">✦</span>
              <span className="text-[11px] font-semibold text-[#EDEDED]">AI Assistant</span>
              <button onClick={() => { setAiAssist(null); setAiAssistResult(null); }} className="ml-auto p-0.5 rounded hover:bg-[#2a2a2a] text-[#52525B]">✕</button>
            </div>

            {/* Selected text preview */}
            <div className="px-3 py-2 border-b border-[#1a1a1a]">
              <p className="text-[10px] text-[#52525B] mb-1">Selected text</p>
              <p className="text-[11px] text-[#A1A1AA] line-clamp-2 italic">"{aiAssist.selectedText.slice(0, 100)}{aiAssist.selectedText.length > 100 ? "…" : ""}"</p>
            </div>

            {/* Action buttons */}
            {!aiAssistResult && !aiAssistLoading && (
              <div className="p-2 grid grid-cols-2 gap-1">
                {[
                  { id: "rewrite", label: "✏️ Rewrite" },
                  { id: "expand", label: "📝 Expand" },
                  { id: "summarize", label: "📋 Summarize" },
                  { id: "fix", label: "🔧 Fix grammar" },
                  { id: "tone_formal", label: "👔 Make formal" },
                  { id: "tone_casual", label: "😊 Make casual" },
                ].map(action => (
                  <button
                    key={action.id}
                    onClick={() => runAiAssist(action.id)}
                    className="px-2 py-1.5 rounded border border-[#2A2A2A] text-[11px] text-[#A1A1AA] hover:bg-[#1a1a1a] hover:text-[#EDEDED] transition-colors text-left"
                  >{action.label}</button>
                ))}
                <div className="col-span-2 flex gap-1">
                  <input
                    value={aiCustomPrompt}
                    onChange={e => setAiCustomPrompt(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && aiCustomPrompt.trim()) runAiAssist("custom"); }}
                    placeholder="Custom instruction…"
                    className="flex-1 px-2 py-1.5 rounded border border-[#2A2A2A] bg-[#0A0A0A] text-[11px] text-[#EDEDED] placeholder-[#52525B] outline-none focus:border-[#5A90F5]"
                  />
                  <button
                    onClick={() => aiCustomPrompt.trim() && runAiAssist("custom")}
                    className="px-2 py-1.5 rounded border border-[#2A2A2A] text-[11px] text-[#5A90F5] hover:bg-[#5A90F5]/10 transition-colors"
                  >→</button>
                </div>
              </div>
            )}

            {/* Loading */}
            {aiAssistLoading && (
              <div className="px-3 py-6 flex items-center justify-center gap-2 text-[#52525B] text-[11px]">
                <span className="animate-spin">⟳</span> Thinking…
              </div>
            )}

            {/* Result */}
            {aiAssistResult && !aiAssistLoading && (
              <div className="p-3 space-y-2">
                <p className="text-[10px] text-[#52525B]">Result</p>
                <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded p-2 text-[11px] text-[#EDEDED] max-h-32 overflow-y-auto leading-relaxed">
                  {aiAssistResult}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={applyAiResult}
                    className="flex-1 px-2 py-1.5 rounded bg-[#5A90F5] text-white text-[11px] font-medium hover:bg-[#4a80e5] transition-colors"
                  >Replace selection</button>
                  <button
                    onClick={() => setAiAssistResult(null)}
                    className="px-2 py-1.5 rounded border border-[#2A2A2A] text-[11px] text-[#A1A1AA] hover:bg-[#1a1a1a] transition-colors"
                  >Try again</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {templatePickerOpen && (
        <DocumentTemplates
          onClose={() => setTemplatePickerOpen(false)}
          onSelect={async (tpl) => {
            setTemplatePickerOpen(false);
            const { data } = await api.post("/documents", { name: tpl.name, kind: "text", body: tpl.body, workspace_id: active?.workspace_id || currentWs?.workspace_id });
            if (data) { create && await create(data); if (editorRef.current) { editorRef.current.innerHTML = tpl.body; setDirty(true); } }
          }}
        />
      )}
      {shareOpen && active && (
        <div className="fixed inset-0 z-[150] bg-black/60 flex items-center justify-center" onClick={() => setShareOpen(false)} data-testid="docs-share-dialog">
          <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-5 max-w-md w-full mx-4 space-y-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-sm font-semibold flex items-center gap-2">
                <Share2 className="w-4 h-4 text-[#5A90F5]" /> Share document
              </h3>
              <button onClick={() => setShareOpen(false)} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <label className="meta-label block mb-1">ACCESS</label>
              <ThemedSelect
                value={shareMode}
                onChange={(v) => setShareMode(v)}
                options={[
                  { value: "private", label: "Private — Only you" },
                  { value: "public", label: "Public — Anyone with the link" },
                  { value: "invite", label: "Invite — Listed people only" },
                  { value: "secured", label: "Secure — Invite + password" },
                ]}
                className="w-full"
                data-testid="docs-share-mode"
              />
            </div>
            {(shareMode === "invite" || shareMode === "secured") && (
              <div>
                <label className="meta-label block mb-1">INVITED PEOPLE</label>
                <div
                  className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md min-h-[40px] focus-within:border-[#5A90F5]"
                  data-testid="docs-share-invitees"
                >
                  {shareInvitees.map((it, idx) => (
                    <span key={`${it.email}-${idx}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-[#1F2937] border border-[#2A2A2A] text-[11px]">
                      <span className="text-[#EDEDED]">{it.email}</span>
                      <select
                        value={it.role}
                        onChange={(e) => setShareInvitees((arr) => arr.map((x, i) => i === idx ? { ...x, role: e.target.value } : x))}
                        className="bg-transparent text-[10px] uppercase tracking-wider text-[#5A90F5] outline-none cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                      </select>
                      <button
                        onClick={() => setShareInvitees((arr) => arr.filter((_, i) => i !== idx))}
                        className="px-1 rounded hover:bg-[#2a2a2a] text-[#A1A1AA]"
                      >×</button>
                    </span>
                  ))}
                  <input
                    value={pillDraft}
                    onChange={(e) => setPillDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
                        if (pillDraft.trim()) { e.preventDefault(); addPill(); }
                      }
                      if (e.key === "Backspace" && !pillDraft && shareInvitees.length > 0) {
                        setShareInvitees((arr) => arr.slice(0, -1));
                      }
                    }}
                    onBlur={() => pillDraft && addPill()}
                    placeholder={shareInvitees.length === 0 ? "name@example.com — Enter or comma to add" : ""}
                    className="flex-1 min-w-[140px] bg-transparent outline-none text-[12px] text-[#EDEDED]"
                    data-testid="docs-share-invitee-draft"
                  />
                </div>
              </div>
            )}
            {shareMode === "secured" && (
              <div>
                <label className="meta-label block mb-1">PASSWORD</label>
                <input
                  type="password"
                  value={sharePassword}
                  onChange={(e) => setSharePassword(e.target.value)}
                  placeholder={active.share_password_hash ? "Already set — type to change" : "Set a password"}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555] text-xs font-mono"
                  data-testid="docs-share-password"
                />
              </div>
            )}
            {shareMode !== "private" && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="meta-label">SHARE LINK</div>
                  {shareToken && (
                    <button
                      onClick={async () => {
                        const ok = await confirmDelete({ title: "Rotate share token?", message: "The current link will stop working immediately. Anyone with the old link will lose access.", confirmLabel: "Rotate" });
                        if (!ok) return;
                        try {
                          // Force-rotate by toggling private→current mode.
                          await api.put(`/documents/${active.doc_id}/share`, { mode: "private" });
                          const body = { mode: shareMode };
                          if (shareMode !== "private") body.invitees = shareInvitees.map((x) => ({ email: x.email, role: x.role || "viewer" }));
                          if (shareMode === "secured" && sharePassword) body.password = sharePassword;
                          const { data } = await api.put(`/documents/${active.doc_id}/share`, body);
                          if (data.share_token) setShareToken(data.share_token);
                          toast.success("New share link generated");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Couldn't rotate");
                        }
                      }}
                      className="text-[10px] text-[#FF8A00] hover:underline"
                      data-testid="docs-share-rotate"
                    >Rotate</button>
                  )}
                </div>
                <div className="rounded-md border border-[#2A2A2A] bg-[#0A0A0A] p-2.5">
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={shareUrl || `${window.location.origin}/share/doc/…`}
                    className="flex-1 bg-transparent text-[11px] font-mono outline-none text-[#EDEDED]"
                    data-testid="docs-share-link"
                  />
                  <button
                    onClick={async () => { if (shareUrl) { const ok = await copyToClipboard(shareUrl); toast[ok?"success":"error"](ok?"Copied!":"Copy failed"); } else { toast.message("Click Save to generate the link"); } }}
                    className="px-3 py-1 rounded bg-white text-[#0A0A0A] text-[11px] font-semibold hover:bg-[#E7ECFA]"
                    data-testid="docs-share-copy"
                  >Copy</button>
                </div>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShareOpen(false)} className="px-3 py-1.5 text-xs text-[#A1A1AA] hover:bg-[#1a1a1a] rounded">Cancel</button>
              <button
                onClick={saveShare}
                disabled={shareSaving}
                className="px-3 py-1.5 text-xs rounded bg-[#EDEDED] text-[#0A0A0A] font-medium hover:bg-white disabled:opacity-60"
                data-testid="docs-share-save"
              >
                {shareSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── InkOverlay ────────────────────────────────────────────────────────────────
// Transparent canvas over the document. Strokes accumulate across finger lifts.
// After 1.5s of no drawing, auto-recognizes and inserts text.
// "Done" button forces immediate recognition.
function InkOverlay({ editorRef, onInsertText, onClose }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const fingerPos = useRef({ x: 0, y: 0 });
  const brushPos = useRef({ x: 0, y: 0 });
  const lastBrush = useRef({ x: 0, y: 0 });
  const rafId = useRef(null);
  const debounceRef = useRef(null);
  const hasStrokes = useRef(false);
  const initialized = useRef(false);
  const colorRef = useRef("#000000");
  const sizeRef = useRef(3);
  const smoothingRef = useRef(0.85);
  const [inkColor, setInkColor] = useState("#000000");
  const [inkSize, setInkSize] = useState(3);
  const [inkSmoothing, setInkSmoothing] = useState(0.85);

  const setColor = v => { colorRef.current = v; setInkColor(v); };
  const setSize = v => { sizeRef.current = v; setInkSize(v); };
  const setSmoothing = v => { smoothingRef.current = v; setInkSmoothing(v); };
  const [recognizing, setRecognizing] = useState(false);
  const [status, setStatus] = useState("");
  const [writeToText, setWriteToText] = useState(false);
  const writeToTextRef = useRef(false);
  const _setWriteToText = v => { writeToTextRef.current = v; setWriteToText(v); };

  const initCanvas = (canvas) => {
    if (!canvas || initialized.current) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const w = rect.width || parent.offsetWidth || 400;
    const h = rect.height || parent.offsetHeight || 300;
    if (w < 10 || h < 10) return; // not laid out yet
    const scale = window.devicePixelRatio || 1;
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);
    initialized.current = true;
  };

  const captureAndRecognize = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasStrokes.current) return;
    setRecognizing(true);
    setStatus("Reading…");
    try {
      const scale = window.devicePixelRatio || 1;
      const cssW = canvas.width / scale;
      const cssH = canvas.height / scale;
      const tmp = document.createElement("canvas");
      tmp.width = Math.round(cssW);
      tmp.height = Math.round(cssH);
      const tc = tmp.getContext("2d");
      tc.fillStyle = "#ffffff";
      tc.fillRect(0, 0, tmp.width, tmp.height);
      tc.drawImage(canvas, 0, 0, tmp.width, tmp.height);
      const base64 = tmp.toDataURL("image/png").split(",")[1];

      const clear = () => {
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, cssW, cssH);
        hasStrokes.current = false;
        initialized.current = false;
        setStatus("");
      };

      // 1. Plain handwriting recognition — always gets ALL the text
      const { data } = await api.post("/ai/handwriting", { image: base64, mime: "image/png" });
      let text = data.text?.trim();

      if (!text) {
        // Nothing readable — try visual shorthand match as last resort
        try {
          const { data: sh } = await api.post("/ai/recognize-shorthand", { image: base64, mime: "image/png" });
          if (sh?.match && sh.confidence > 0.5) {
            clear(); onInsertText(sh.match); setRecognizing(false); return;
          }
        } catch { /* no written shorthands */ }
        setStatus("Couldn't read — try writing larger");
        setRecognizing(false);
        return;
      }

      // 2. Expand any typed shorthand triggers found in the recognized text
      try {
        const { data: shList } = await api.get("/me/shorthands");
        if (Array.isArray(shList)) {
          for (const sh of shList) {
            if (sh.kind !== "written" && sh.trigger && sh.expansion) {
              const re = new RegExp(sh.trigger.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
              text = text.replace(re, sh.expansion);
            }
          }
        }
      } catch { /* shorthand expansion optional */ }

      clear();
      onInsertText(text);
    } catch (err) {
      const s = err?.response?.status;
      const d = err?.response?.data?.detail || err?.message || "unknown";
      if (s === 404) setStatus("Deploy latest backend zip");
      else if (s === 502) setStatus(`Vision error: ${d.slice(0, 50)}`);
      else if (s === 401) setStatus("Not logged in");
      else setStatus(`Error ${s || "?"}: ${d.slice(0, 50)}`);
    }
    setRecognizing(false);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Try to init immediately, retry via RAF if not ready
    const tryInit = () => {
      if (!initialized.current) {
        initCanvas(canvas);
        if (!initialized.current) requestAnimationFrame(tryInit);
      }
    };
    tryInit();

    const animLoop = () => {
      if (!drawing.current) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const bx = brushPos.current.x + (fingerPos.current.x - brushPos.current.x) * (1 - smoothingRef.current);
      const by = brushPos.current.y + (fingerPos.current.y - brushPos.current.y) * (1 - smoothingRef.current);
      const dx = bx - lastBrush.current.x, dy = by - lastBrush.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > 0.3) {
        const ctx = canvas.getContext("2d");
        ctx.strokeStyle = colorRef.current;
        ctx.lineWidth = sizeRef.current;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.moveTo(lastBrush.current.x, lastBrush.current.y);
        ctx.lineTo(bx, by);
        ctx.stroke();
        lastBrush.current = { x: bx, y: by };
        hasStrokes.current = true;
      }
      brushPos.current = { x: bx, y: by };
      rafId.current = requestAnimationFrame(animLoop);
    };

    const getXY = e => {
      const r = canvas.getBoundingClientRect();
      const src = e.touches ? e.touches[0] : e;
      return { x: src.clientX - r.left, y: src.clientY - r.top };
    };

    const onStart = e => {
      e.preventDefault(); e.stopPropagation();
      if (!initialized.current) initCanvas(canvas);
      clearTimeout(debounceRef.current);
      drawing.current = true;
      const p = getXY(e);
      fingerPos.current = brushPos.current = lastBrush.current = p;
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(animLoop);
    };
    const onMove = e => { e.preventDefault(); if (drawing.current) fingerPos.current = getXY(e); };
    const onEnd = e => {
      e.preventDefault();
      drawing.current = false;
      cancelAnimationFrame(rafId.current);
      if (hasStrokes.current && writeToTextRef.current) {
        setStatus("Will convert in 1.5s…");
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(captureAndRecognize, 1500);
      }
    };

    canvas.addEventListener("touchstart", onStart, { passive: false });
    canvas.addEventListener("touchmove", onMove, { passive: false });
    canvas.addEventListener("touchend", onEnd, { passive: false });
    canvas.addEventListener("mousedown", onStart);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseup", onEnd);

    return () => {
      clearTimeout(debounceRef.current);
      cancelAnimationFrame(rafId.current);
      canvas.removeEventListener("touchstart", onStart);
      canvas.removeEventListener("touchmove", onMove);
      canvas.removeEventListener("touchend", onEnd);
      canvas.removeEventListener("mousedown", onStart);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseup", onEnd);
    };
  }, []);

  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 100 }}>
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", touchAction: "none", cursor: "crosshair" }}
      />

      {/* Bottom floating toolbar */}
      <div
        className="absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none"
        onTouchStart={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 bg-[#0A0A0A]/95 border border-[#2A2A2A] rounded-2xl px-4 py-2.5 pointer-events-auto shadow-2xl flex-wrap justify-center max-w-full mx-4">

          {/* Colors */}
          {["#000000","#1a1aff","#cc0000","#007700","#ff8800","#888888"].map(c => (
            <button key={c} onPointerDown={e => e.stopPropagation()} onClick={() => setColor(c)}
              style={{ width: 20, height: 20, borderRadius: "50%", background: c, flexShrink: 0,
                outline: inkColor === c ? `3px solid ${c === "#000000" ? "#fff" : c}` : "none", outlineOffset: 2, cursor: "pointer" }}/>
          ))}
          <input type="color" value={inkColor} onPointerDown={e => e.stopPropagation()}
            onChange={e => setColor(e.target.value)}
            style={{ width: 22, height: 22, cursor: "pointer", border: "none", background: "transparent", padding: 0 }}/>

          <div className="w-px h-5 bg-[#2A2A2A] flex-shrink-0"/>

          {/* Size */}
          {[1.5, 3, 5, 9].map(s => (
            <button key={s} onPointerDown={e => e.stopPropagation()} onClick={() => setSize(s)}
              className={`flex items-center justify-center rounded-full flex-shrink-0 ${inkSize === s ? "bg-[#2A2A2A]" : ""}`}
              style={{ width: 26, height: 26, cursor: "pointer" }}>
              <div style={{ width: Math.min(s * 2, 16), height: Math.min(s * 2, 16), borderRadius: "50%", background: inkColor || "#000" }}/>
            </button>
          ))}

          <button onPointerDown={e => e.stopPropagation()}
            onClick={() => _setWriteToText(v => !v)}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors flex-shrink-0 ${writeToText ? "bg-[#5A90F5] text-white" : "border border-[#2A2A2A] text-[#52525B]"}`}>
            Write to text: {writeToText ? "ON" : "OFF"}
          </button>

          <div className="w-px h-5 bg-[#2A2A2A] flex-shrink-0"/>
          <span className="text-[9px] text-[#52525B] flex-shrink-0">Smooth</span>
          <input type="range" min="0" max="0.97" step="0.01" value={inkSmoothing}
            onPointerDown={e => e.stopPropagation()}
            onChange={e => setSmoothing(parseFloat(e.target.value))}
            className="w-20 accent-[#5A90F5] flex-shrink-0" style={{ height: 4 }}/>

          <div className="w-px h-5 bg-[#2A2A2A] flex-shrink-0"/>

          {/* Actions */}
          {recognizing ? (
            <span className="text-[11px] text-[#5A90F5] flex items-center gap-1 flex-shrink-0">
              <div className="w-2 h-2 rounded-full bg-[#5A90F5] animate-pulse"/> Reading…
            </span>
          ) : (
            <span className="text-[10px] text-[#3A3A3A] flex-shrink-0 max-w-[80px] truncate">{status}</span>
          )}

          <button onPointerDown={e => e.stopPropagation()}
            onClick={() => { clearTimeout(debounceRef.current); captureAndRecognize(); }}
            disabled={recognizing}
            className="px-2.5 py-1 rounded-lg bg-[#5A90F5] text-white text-[11px] font-medium disabled:opacity-40 flex-shrink-0">
            Convert
          </button>
          <button onPointerDown={e => e.stopPropagation()} onClick={onClose}
            className="text-[#52525B] hover:text-[#EDEDED] text-[11px] px-2 py-1 rounded-lg hover:bg-[#141414] flex-shrink-0">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
